/*!
 * ua.fields.schema.editor
 * Platform field-schema-driven editor control.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.fields.schema.editor",
  title: "Schema Editor",
  description: "Edits data documents using a field schema with wizard-based controls.",
  editable: true,
  control: true,
  preview: "ua.preview.json",
  supportServer: false,
  fields: {
    schema: {
      title: "Schema",
      type: "object",
      description: "Full field schema definition.",
      required: true
    },
    data: {
      title: "Data",
      type: "object",
      description: "Initial document data to load."
    },
    continueText: {
      title: "Continue Button Text",
      type: "string",
      description: "Wizard continue button text override."
    },
    submitText: {
      title: "Submit Button Text",
      type: "string",
      description: "Wizard final submit button text override."
    },
    showStepNavigation: {
      title: "Show Step Navigation",
      type: "boolean",
      description: "When true, shows wizard step navigation for this editor.",
      default: true
    },
    showSummaryStep: {
      title: "Show Summary Step",
      type: "boolean",
      description: "When true, includes a summary step in this editor wizard.",
      default: true
    },
    skipGroupedNestedSteps: {
      title: "Skip Grouped Nested Steps",
      type: "boolean",
      description: "When true, nested paths are omitted when their root field is explicitly grouped.",
      default: false
    },
    excludeUngroupedEntries: {
      title: "Exclude Ungrouped Entries",
      type: "boolean",
      description: "When true, only explicitly grouped fields are rendered as wizard steps.",
      default: false
    },
    disableDefaultWizardBehavior: {
      title: "Disable Default Wizard Behavior",
      type: "boolean",
      description: "When true, keeps wizard save semantics but disables default wizard UI controls for embedded contexts.",
      default: false
    },
    schemaDocumentMode: {
      title: "Schema Document Mode",
      type: "boolean",
      description: "When true, edits direct field-schema documents as keyed field-definition maps.",
      default: false
    }
  },
  errors: {
    missingSchema: {
      title: "Missing Schema",
      description: "A schema object is required to use this editor."
    },
    invalidJson: {
      title: "Invalid JSON",
      description: "The value must be valid JSON."
    }
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");
var fieldsInclude = require("../../../.includes/fields.js");

var CROSS_ORIGIN_OPTIONS = ["", "anonymous", "use-credentials", "*"];
var REFERRER_POLICY_OPTIONS = [
  "",
  "no-referrer",
  "no-referrer-when-downgrade",
  "origin",
  "origin-when-cross-origin",
  "same-origin",
  "strict-origin",
  "strict-origin-when-cross-origin",
  "unsafe-url"
];

var FIELD_SCHEMA_CORE_KEYS = ["name", "title", "description", "type"];
var FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE = "field.schema.document";

function stripUndefinedProperties(value) {
  if (Array.isArray(value)) {
    var nextList = [];
    for (var i = 0; i < value.length; i++) {
      if (value[i] === undefined) continue;
      nextList.push(stripUndefinedProperties(value[i]));
    }
    return nextList;
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  var next = {};
  var keys = Object.keys(value);
  for (var k = 0; k < keys.length; k++) {
    var key = keys[k];
    if (value[key] === undefined) continue;
    next[key] = stripUndefinedProperties(value[key]);
  }
  return next;
}

function getRegistryList(registry) {
  if (!registry || typeof registry.list !== "function") return [];
  try {
    return registry.list();
  } catch (error) {
    return [];
  }
}

function buildFieldCatalog() {
  var catalog = [];
  var registry = fieldsInclude && fieldsInclude._ && fieldsInclude._.registry;
  var namespaces = getRegistryList(registry);

  for (var i = 0; i < namespaces.length; i++) {
    var ns = namespaces[i];
    if (!ns) continue;

    var mod = null;
    try {
      mod = registry.find(ns);
    } catch (error) {
      mod = null;
    }
    if (!mod || !mod.define) continue;
    if (mod.define.type !== "field") continue;

    catalog.push({
      namespace: mod.define.namespace || ns,
      title: safeDisplayText(mod.define.title, mod.define.namespace || ns),
      description: safeDisplayText(mod.define.description, ""),
      showInEditor: mod.define.showInEditor !== false
    });
  }

  return catalog;
}

function buildFieldAttributeMap() {
  var map = {};
  var registry = fieldsInclude && fieldsInclude._ && fieldsInclude._.registry;
  var namespaces = getRegistryList(registry);

  for (var i = 0; i < namespaces.length; i++) {
    var ns = namespaces[i];
    if (!ns) continue;

    var mod = null;
    try {
      mod = registry.find(ns);
    } catch (error) {
      mod = null;
    }
    if (!mod || !mod.define || mod.define.type !== "field") continue;

    var typeNs = normalizeSingleType(mod.define.namespace || ns);
    var attrs = [];
    var fields = (mod.define.fields && typeof mod.define.fields === "object") ? mod.define.fields : {};
    var keys = Object.keys(fields);
    for (var k = 0; k < keys.length; k++) {
      var attr = (keys[k] || "").toString().trim();
      if (!attr) continue;
      if (attrs.indexOf(attr) >= 0) continue;
      attrs.push(attr);
    }
    map[typeNs] = attrs;
  }

  return map;
}

function cloneFieldDefinition(definition, fallbackTitle, fallbackDescription) {
  var next = (definition && typeof definition === "object") ? Object.assign({}, definition) : {};
  next.title = safeDisplayText(next.title, safeDisplayText(fallbackTitle, ""));
  next.description = safeDisplayText(next.description, safeDisplayText(fallbackDescription, ""));
  if (!next.type) {
    next.type = "any";
  }
  return next;
}

function normalizeFieldAttributeDefinitionForType(typeNs, attrKey, definition) {
  var normalizedType = normalizeSingleType(typeNs);
  var normalizedAttrKey = safeDisplayText(attrKey, "").trim();
  var next = cloneFieldDefinition(definition, normalizedAttrKey, "");

  if (normalizedType === "string") {
    if (normalizedAttrKey === "min") {
      next.title = "Minimum Length";
      if (!safeDisplayText(next.description, "").trim()) {
        next.description = "The minimum number of characters allowed.";
      }
    } else if (normalizedAttrKey === "max") {
      next.title = "Maximum Length";
      if (!safeDisplayText(next.description, "").trim()) {
        next.description = "The maximum number of characters allowed.";
      }
    }
  }

  return next;
}

function getAttributeKeyFromNamespace(namespace) {
  var ns = (namespace || "").toString().trim();
  if (!ns) return "";
  if (ns.indexOf("attr.") === 0) {
    return ns.slice("attr.".length);
  }
  return "";
}

function buildGlobalAttributeDefinitions() {
  var map = {};
  var registry = fieldsInclude && fieldsInclude._ && fieldsInclude._.registry;
  var namespaces = getRegistryList(registry);

  for (var i = 0; i < namespaces.length; i++) {
    var ns = namespaces[i];
    if (!ns) continue;

    var mod = null;
    try {
      mod = registry.find(ns);
    } catch (error) {
      mod = null;
    }
    if (!mod || !mod.define || mod.define.type !== "attribute") continue;

    var attrKey = getAttributeKeyFromNamespace(mod.define.namespace || ns);
    if (!attrKey) continue;
    if (map[attrKey]) continue;

    var attributeFields = (mod.define.fields && typeof mod.define.fields === "object") ? mod.define.fields : {};
    var attributeFieldKeys = Object.keys(attributeFields);
    var attributeDefinition = null;

    if (attributeFieldKeys.length > 0) {
      if (attributeFields[attrKey] && typeof attributeFields[attrKey] === "object") {
        attributeDefinition = cloneFieldDefinition(
          attributeFields[attrKey],
          mod.define.title || attrKey,
          mod.define.description || ""
        );
      } else {
        attributeDefinition = {
          title: safeDisplayText(mod.define.title, attrKey),
          description: safeDisplayText(mod.define.description, ""),
          type: "object",
          fields: attributeFields
        };
      }
    } else {
      attributeDefinition = {
        title: safeDisplayText(mod.define.title, attrKey),
        description: safeDisplayText(mod.define.description, ""),
        type: attrKey === "required" ? "boolean" : "any"
      };
    }

    map[attrKey] = stripUndefinedProperties(attributeDefinition);
  }

  return map;
}

function buildFieldTypeAttributeDefinitions() {
  var map = {};
  var registry = fieldsInclude && fieldsInclude._ && fieldsInclude._.registry;
  var namespaces = getRegistryList(registry);

  for (var i = 0; i < namespaces.length; i++) {
    var ns = namespaces[i];
    if (!ns) continue;

    var mod = null;
    try {
      mod = registry.find(ns);
    } catch (error) {
      mod = null;
    }
    if (!mod || !mod.define || mod.define.type !== "field") continue;

    var typeNs = normalizeSingleType(mod.define.namespace || ns);
    var fields = (mod.define.fields && typeof mod.define.fields === "object") ? mod.define.fields : {};
    var keys = Object.keys(fields);
    var defs = {};

    for (var k = 0; k < keys.length; k++) {
      var attr = (keys[k] || "").toString().trim();
      if (!attr) continue;
      defs[attr] = normalizeFieldAttributeDefinitionForType(typeNs, attr, fields[attr]);
    }

    map[typeNs] = defs;
  }

  return map;
}

function createBaseFieldDefinitionSchema() {
  return {
    name: {
      title: "Field Key",
      description: "Unique key used in the saved schema document.",
      type: "string",
      required: true
    },
    title: {
      title: "Field Title",
      description: "Human-friendly label shown to editors and users.",
      type: "string",
      required: true
    },
    description: {
      title: "Description",
      description: "Optional helper text describing this field.",
      type: "string"
    },
    type: {
      title: "Field Type",
      description: "Choose one or more value types for this field.",
      type: "field.type",
      required: true
    }
  };
}

function buildFieldDefinitionEditorSchema(typeCatalog, typeAttributeMap, typeAttributeDefinitions, globalAttributeDefinitions) {
  return enrichFieldSchemaForEditor(
    createBaseFieldDefinitionSchema(),
    typeCatalog,
    typeAttributeMap,
    typeAttributeDefinitions,
    globalAttributeDefinitions
  );
}

function enrichFieldSchemaForEditor(schema, typeCatalog, typeAttributeMap, typeAttributeDefinitions, globalAttributeDefinitions) {
  if (!hasFieldSchemaCore(schema)) return schema;

  var next = {};
  var sourceKeys = Object.keys(schema || {});
  for (var i = 0; i < sourceKeys.length; i++) {
    var key = sourceKeys[i];
    next[key] = cloneFieldDefinition((schema || {})[key], key, "");
  }

  var fallbackGlobals = {
    required: {
      title: "Required",
      description: "Whether this field is required.",
      type: "boolean"
    },
    default: {
      title: "Default Value",
      description: "Default value used when no explicit value is provided.",
      type: "any"
    },
    list: {
      title: "List",
      description: "Optional allowed values list for this field.",
      type: "any"
    },
    fields: {
      title: "Fields",
      description: "Nested field schema used by object-style field types.",
      type: "object"
    }
  };

  var globalKeys = Object.keys(fallbackGlobals);
  for (var g = 0; g < globalKeys.length; g++) {
    var globalKey = globalKeys[g];
    if (next[globalKey]) continue;

    if (globalAttributeDefinitions && globalAttributeDefinitions[globalKey]) {
      next[globalKey] = cloneFieldDefinition(globalAttributeDefinitions[globalKey], globalKey, "");
    } else {
      next[globalKey] = cloneFieldDefinition(fallbackGlobals[globalKey], globalKey, "");
    }
  }

  for (var t = 0; t < typeCatalog.length; t++) {
    var typeRow = typeCatalog[t] || {};
    var typeNs = normalizeSingleType(typeRow.namespace || "");
    if (!typeNs) continue;

    var attrs = (typeAttributeMap && typeAttributeMap[typeNs]) ? typeAttributeMap[typeNs] : [];
    var defs = (typeAttributeDefinitions && typeAttributeDefinitions[typeNs]) ? typeAttributeDefinitions[typeNs] : {};
    for (var a = 0; a < attrs.length; a++) {
      var attrKey = attrs[a];
      if (!attrKey) continue;
      var attrDef = defs[attrKey];
      if (!attrDef) continue;

      if (!next[attrKey]) {
        next[attrKey] = cloneFieldDefinition(attrDef, attrKey, "");
        continue;
      }

      if (!next[attrKey].title || next[attrKey].title === attrKey) {
        next[attrKey].title = safeDisplayText(attrDef.title, next[attrKey].title || attrKey);
      }
      if (!next[attrKey].description) {
        next[attrKey].description = safeDisplayText(attrDef.description, "");
      }
      if (!next[attrKey].type) {
        next[attrKey].type = attrDef.type || "any";
      }
    }
  }

  if (next.fields) {
    next.fields = Object.assign({}, next.fields, {
      type: FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE,
      schemaDocument: schema
    });
  }

  return next;
}

function hasFieldSchemaCore(schema) {
  if (!(schema && typeof schema === "object") || Array.isArray(schema)) return false;
  var keys = Object.keys(schema);
  for (var i = 0; i < FIELD_SCHEMA_CORE_KEYS.length; i++) {
    if (keys.indexOf(FIELD_SCHEMA_CORE_KEYS[i]) < 0) return false;
  }
  return true;
}

function hasFieldSchemaDocumentEntry(schema) {
  if (!(schema && typeof schema === "object") || Array.isArray(schema)) return false;
  return !!safeDisplayText(schema.type, "").trim();
}

function isFieldSchemaDocument(schema) {
  if (!(schema && typeof schema === "object") || Array.isArray(schema)) return false;
  if (hasFieldSchemaCore(schema)) return false;

  var keys = Object.keys(schema);
  if (keys.length === 0) return false;

  for (var i = 0; i < keys.length; i++) {
    var value = schema[keys[i]];
    if (!hasFieldSchemaDocumentEntry(value)) return false;
  }

  return true;
}

function cloneData(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    var list = [];
    for (var i = 0; i < value.length; i++) {
      list.push(cloneData(value[i]));
    }
    return list;
  }

  var prototype = Object.getPrototypeOf(value);
  var next = prototype === null ? Object.create(null) : {};
  var keys = Object.keys(value);
  for (var k = 0; k < keys.length; k++) {
    next[keys[k]] = cloneData(value[keys[k]]);
  }
  return next;
}

function createStringKeyMap() {
  return Object.create(null);
}

function createPathContainer() {
  return Object.create(null);
}

function hasOwnKey(target, key) {
  return !!(target && Object.prototype.hasOwnProperty.call(target, key));
}

function convertSchemaDocumentToFieldList(document) {
  if (!isFieldSchemaDocument(document)) return [];

  var keys = Object.keys(document);
  var list = [];
  for (var i = 0; i < keys.length; i++) {
    var fieldName = keys[i];
    var definition = cloneData(document[fieldName]) || {};
    if (!definition.name) definition.name = fieldName;
    list.push(definition);
  }
  return list;
}

function convertFieldListToSchemaDocument(list) {
  var out = createStringKeyMap();
  if (!(list instanceof Array)) return out;

  for (var i = 0; i < list.length; i++) {
    var item = (list[i] && typeof list[i] === "object" && !Array.isArray(list[i])) ? cloneData(list[i]) : null;
    if (!item) continue;

    var fieldName = safeDisplayText(item.name, "").trim();
    if (!fieldName) continue;

    delete item.name;
    out[fieldName] = item;
  }

  return out;
}

function isFieldSchemaDocumentList(list) {
  if (!(list instanceof Array) || list.length === 0) return false;

  for (var i = 0; i < list.length; i++) {
    var item = (list[i] && typeof list[i] === "object" && !Array.isArray(list[i])) ? list[i] : null;
    if (!item) return false;

    var fieldName = safeDisplayText(item.name, "").trim();
    if (!fieldName) return false;
    if (!hasFieldSchemaDocumentEntry(item)) return false;
  }

  return true;
}

function collectSchemaDocumentFieldListErrors(list) {
  var errors = [];
  if (!(list instanceof Array)) return errors;

  var seen = createStringKeyMap();
  for (var i = 0; i < list.length; i++) {
    var item = (list[i] && typeof list[i] === "object" && !Array.isArray(list[i])) ? list[i] : null;
    if (!item) continue;

    var fieldName = safeDisplayText(item.name, "").trim();
    if (!fieldName) continue;

    var normalizedFieldName = fieldName.toLowerCase();
    if (hasOwnKey(seen, normalizedFieldName)) {
      errors.push(createValidationError(
        "Field names must be unique. Duplicate name: " + fieldName,
        "fields[" + i + "].name"
      ));
      continue;
    }

    seen[normalizedFieldName] = fieldName;
  }

  return errors;
}

function inferEntryVisibleTypes(entry, typeAttributeMap) {
  if (!entry || !typeAttributeMap) return [];
  if (entry.path.length !== 1) return [];

  if (FIELD_SCHEMA_CORE_KEYS.indexOf(entry.key) >= 0) {
    return [];
  }
  if (entry.key === "required" || entry.key === "default" || entry.key === "list") {
    return [];
  }
  if (entry.key === "fields") {
    return ["list", "object"];
  }

  var matches = [];
  var types = Object.keys(typeAttributeMap);
  for (var i = 0; i < types.length; i++) {
    var typeNs = types[i];
    var attrs = typeAttributeMap[typeNs] || [];
    if (attrs.indexOf(entry.key) >= 0) {
      matches.push(typeNs);
    }
  }

  if (matches.length === 0) {
    return [];
  }
  return matches;
}

function normalizeTypeSelection(value) {
  if (Array.isArray(value)) {
    var out = [];
    for (var i = 0; i < value.length; i++) {
      var typeNs = normalizeSingleType(value[i]);
      if (out.indexOf(typeNs) >= 0) continue;
      out.push(typeNs);
    }
    return out;
  }

  var single = normalizeSingleType(value);
  if (!single) return [];
  return [single];
}

function normalizeType(typeValue) {
  if (typeValue && typeof typeValue === "object" && !Array.isArray(typeValue)) {
    return normalizeType(typeValue.type);
  }

  if (Array.isArray(typeValue)) {
    var result = [];
    for (var i = 0; i < typeValue.length; i++) {
      var normalized = normalizeSingleType(typeValue[i]);
      if (result.indexOf(normalized) === -1) {
        result.push(normalized);
      }
    }
    return result;
  }

  return [normalizeSingleType(typeValue)];
}

function normalizeSingleType(typeValue) {
  if (typeValue && typeof typeValue === "object" && !Array.isArray(typeValue)) {
    return normalizeSingleType(typeValue.type);
  }

  if (typeof typeValue !== "string") {
    return "any";
  }

  var value = typeValue.toLowerCase().trim();
  if (value === "text") return "string";
  if (value === "array") return "list";
  if (value === "*") return "any";
  return value || "any";
}

function encodePath(path) {
  var joined = path.join("__");
  return joined.replace(/[^a-zA-Z0-9_]/g, "_");
}

function safeDisplayText(value, fallback) {
  if (fallback === undefined) fallback = "";
  if (value === undefined || value === null) return fallback;
  var text = value.toString();
  var normalized = text.trim().toLowerCase();
  if (normalized === "undefined" || normalized === "null") return fallback;
  return text;
}

function getByPath(source, path) {
  var cursor = source;
  for (var i = 0; i < path.length; i++) {
    if (!cursor || typeof cursor !== "object") {
      return undefined;
    }

    cursor = cursor[path[i]];
  }

  return cursor;
}

function getRelativePath(basePath, targetPath) {
  if (!(basePath instanceof Array) || !(targetPath instanceof Array)) return [];
  if (basePath.length > targetPath.length) return [];
  for (var i = 0; i < basePath.length; i++) {
    if (String(basePath[i]) !== String(targetPath[i])) return [];
  }
  return targetPath.slice(basePath.length);
}

function setByPath(target, path, value) {
  var cursor = target;

  for (var i = 0; i < path.length - 1; i++) {
    var key = path[i];

    if (!cursor[key] || typeof cursor[key] !== "object" || Array.isArray(cursor[key])) {
      cursor[key] = createPathContainer();
    }

    cursor = cursor[key];
  }

  cursor[path[path.length - 1]] = value;
}

function deleteByPath(target, path) {
  if (!(target && typeof target === "object") || !(path instanceof Array) || path.length === 0) {
    return;
  }

  var cursor = target;
  for (var i = 0; i < path.length - 1; i++) {
    if (!(cursor && typeof cursor === "object")) return;
    cursor = cursor[path[i]];
  }

  if (!(cursor && typeof cursor === "object")) return;
  delete cursor[path[path.length - 1]];
}

function isBlank(value) {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

function valuesEqual(left, right) {
  if (left === right) return true;

  if (left === null || right === null) {
    return left === right;
  }

  if (typeof left !== typeof right) {
    return false;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;

    if (isFieldSchemaDocumentList(left) && isFieldSchemaDocumentList(right)) {
      return valuesEqual(
        convertFieldListToSchemaDocument(left),
        convertFieldListToSchemaDocument(right)
      );
    }

    if (left.length !== right.length) return false;

    for (var i = 0; i < left.length; i++) {
      if (!valuesEqual(left[i], right[i])) return false;
    }
    return true;
  }

  if (left && typeof left === "object") {
    var leftKeys = Object.keys(left);
    var rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;

    leftKeys.sort();
    rightKeys.sort();

    for (var k = 0; k < leftKeys.length; k++) {
      if (leftKeys[k] !== rightKeys[k]) return false;
      if (!valuesEqual(left[leftKeys[k]], right[rightKeys[k]])) return false;
    }
    return true;
  }

  return false;
}

function inferTypeFromValue(value, options) {
  if (value === undefined || value === null) {
    return options[0];
  }

  if (Array.isArray(value) && options.indexOf("list") >= 0) {
    return "list";
  }

  if (typeof value === "boolean" && options.indexOf("boolean") >= 0) {
    return "boolean";
  }

  if (typeof value === "number" && options.indexOf("number") >= 0) {
    return "number";
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    if (
      options.indexOf(FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE) >= 0 &&
      isFieldSchemaDocument(value)
    ) {
      return FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE;
    }

    if (options.indexOf("object") >= 0) {
      return "object";
    }

    if (options.indexOf(FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE) >= 0) {
      return FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE;
    }
  }

  if (typeof value === "string") {
    if (options.indexOf("datetime") >= 0 && /^[0-9]{14}$/.test(value)) {
      return "datetime";
    }

    if (options.indexOf("date") >= 0 && /^[0-9]{8}$/.test(value)) {
      return "date";
    }

    if (options.indexOf("time") >= 0 && /^[0-9]{4,6}$/.test(value)) {
      return "time";
    }

    if (options.indexOf("string") >= 0) {
      return "string";
    }
  }

  if (options.indexOf("any") >= 0) {
    return "any";
  }

  return options[0];
}

function toHumanDatetimeValue(value) {
  var text = (value || "").toString().trim();
  if (!/^[0-9]{14}$/.test(text)) return "";

  var year = text.slice(0, 4);
  var month = text.slice(4, 6);
  var day = text.slice(6, 8);
  var hour = text.slice(8, 10);
  var minute = text.slice(10, 12);

  return year + "-" + month + "-" + day + "T" + hour + ":" + minute;
}

function fromHumanDatetimeValue(value) {
  var text = (value || "").toString().trim();
  if (!text) return "";
  var match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/);
  if (!match) return text;
  return match[1] + match[2] + match[3] + match[4] + match[5] + "00";
}

function getControlMetaForType(type, spec) {
  switch (type) {
    case "boolean":
      return {
        namespace: "ua.checkbox",
        build: function (baseOptions) {
          return {
            n: "ua.checkbox",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            value: baseOptions.value,
            description: baseOptions.description
          };
        }
      };

    case "color":
      return {
        namespace: "ua.select.color",
        build: function (baseOptions) {
          return {
            n: "ua.select.color",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            value: baseOptions.value,
            allowOpacity: spec && spec.allowTransparency === true,
            description: baseOptions.description
          };
        }
      };

    case "date":
      return {
        namespace: "ua.select.date",
        build: function (baseOptions) {
          return {
            n: "ua.select.date",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            value: baseOptions.value,
            min: spec ? spec.min : undefined,
            max: spec ? spec.max : undefined,
            description: baseOptions.description
          };
        }
      };

    case "crossorigin":
      return {
        namespace: "ua.select.dropdown",
        build: function (baseOptions) {
          return {
            n: "ua.select.dropdown",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            data: CROSS_ORIGIN_OPTIONS,
            value: baseOptions.value,
            description: baseOptions.description
          };
        }
      };

    case "referrerpolicy":
      return {
        namespace: "ua.select.dropdown",
        build: function (baseOptions) {
          return {
            n: "ua.select.dropdown",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            data: REFERRER_POLICY_OPTIONS,
            value: baseOptions.value,
            description: baseOptions.description
          };
        }
      };

    case "number":
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "number",
            value: baseOptions.value,
            min: spec ? spec.min : undefined,
            max: spec ? spec.max : undefined,
            step: spec ? spec.step : undefined,
            description: baseOptions.description
          };
        }
      };

    case "object":
      return {
        namespace: "ua.fields.schema.editor",
        build: function (baseOptions) {
          return {
            n: "ua.fields.schema.editor",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            schema: (spec && spec.fields && typeof spec.fields === "object") ? spec.fields : {},
            data: (baseOptions.value && typeof baseOptions.value === "object") ? baseOptions.value : {},
            description: baseOptions.description,
            stablePath: baseOptions.stablePath
          };
        }
      };

    case "inner":
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "text",
            value: baseOptions.value,
            placeholder: "Inner content value",
            description: baseOptions.description
          };
        }
      };

    case "any":
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "text",
            value: baseOptions.value,
            placeholder: "Any value",
            description: baseOptions.description
          };
        }
      };

    case "datetime":
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "datetime-local",
            value: toHumanDatetimeValue(baseOptions.value),
            description: baseOptions.description || "Uses your local timezone in the editor and saves as UTC-style accounting datetime (YYYYMMDDHHMMSS)."
          };
        }
      };

    case "field.type":
      return {
        namespace: "ua.fields.type.select",
        build: function (baseOptions) {
          return {
            n: "ua.fields.type.select",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            value: baseOptions.value,
            description: baseOptions.description,
            types: (spec && spec.typesCatalog instanceof Array) ? spec.typesCatalog : []
          };
        }
      };

    case FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE:
      return {
        namespace: "ua.fields.schema.editor",
        build: function (baseOptions) {
          var schemaDocument = (spec && spec.schemaDocument && typeof spec.schemaDocument === "object") ? spec.schemaDocument : {};
          var fieldDefinitionSchema = buildFieldDefinitionEditorSchema(
            buildFieldCatalog(),
            buildFieldAttributeMap(),
            buildFieldTypeAttributeDefinitions(),
            buildGlobalAttributeDefinitions()
          );
          var normalizedSchema = {
            fields: {
              title: baseOptions.title,
              description: baseOptions.description,
              type: "list",
              of: "object",
              fields: fieldDefinitionSchema,
              schemaDocument: schemaDocument,
              default: convertSchemaDocumentToFieldList(schemaDocument)
            }
          };
          var normalizedData = isFieldSchemaDocument(baseOptions.value)
            ? { fields: convertSchemaDocumentToFieldList(baseOptions.value) }
            : {};
          return {
            n: "ua.fields.schema.editor",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            schema: normalizedSchema,
            data: normalizedData,
            description: baseOptions.description,
            skipGroupedNestedSteps: true,
            showStepNavigation: false,
            showSummaryStep: false,
            disableDefaultWizardBehavior: true,
            schemaDocumentMode: true,
            stablePath: baseOptions.stablePath
          };
        }
      };

    case "time":
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "text",
            placeholder: "HHMMSS",
            value: baseOptions.value,
            description: baseOptions.description || "Format: HHMMSS"
          };
        }
      };

    default:
      return {
        namespace: "ua.input",
        build: function (baseOptions) {
          return {
            n: "ua.input",
            name: baseOptions.name,
            title: baseOptions.title,
            required: baseOptions.required,
            type: "text",
            value: baseOptions.value,
            description: baseOptions.description
          };
        }
      };
  }
}

function shouldInlineNestedBoundary(types, spec) {
  var normalizedTypes = normalizeType(types);
  return normalizedTypes.length === 1 && normalizedTypes[0] === "object" && !(spec && spec.dialog === true);
}

function hasInlineObjectBranch(types, spec) {
  var normalizedTypes = normalizeType(types);
  return (
    normalizedTypes.indexOf("object") >= 0 &&
    normalizedTypes.indexOf(FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE) === -1 &&
    !(spec && spec.dialog === true)
  );
}

function shouldBuildMixedInlineObjectChildren(types) {
  var normalizedTypes = normalizeType(types);
  return (
    normalizedTypes.indexOf("object") >= 0 &&
    normalizedTypes.indexOf(FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE) === -1 &&
    normalizedTypes.indexOf("list") === -1
  );
}

function buildEntries(schema, prefix, entries, options) {
  var keys = Object.keys(schema || {});
  var settings = (options && typeof options === "object") ? options : {};
  var mixedObjectBranchPath = settings.mixedObjectBranchPath instanceof Array ? settings.mixedObjectBranchPath : null;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var spec = schema[key] || {};
    var path = prefix.concat([key]);

    var types = normalizeType(spec.type);
    var hasNestedFields = !!(spec.fields && typeof spec.fields === "object" && Object.keys(spec.fields).length > 0);
    var usesInlineObjectBranch = hasNestedFields && hasInlineObjectBranch(types, spec);
    var usesInlineNestedBoundary = usesInlineObjectBranch && shouldInlineNestedBoundary(types, spec);
    var usesMixedInlineObjectBranch = usesInlineObjectBranch && !usesInlineNestedBoundary && shouldBuildMixedInlineObjectChildren(types);

    var entryTitle = safeDisplayText(spec.title, key || "");

    var entry = {
      index: entries.length,
      key: key,
      path: path,
      pathLabel: "." + path.join("."),
      title: entryTitle,
      description: safeDisplayText(spec.description, ""),
      required: spec.required === true,
      spec: spec,
      types: types,
      nestedOnly: usesInlineNestedBoundary,
      inlineObjectBranch: usesInlineObjectBranch,
      mixedObjectBranchPath: mixedObjectBranchPath ? mixedObjectBranchPath.slice() : null
    };

    entries.push(entry);

    if (usesInlineNestedBoundary) {
      buildEntries(spec.fields, path, entries);
      continue;
    }

    if (usesMixedInlineObjectBranch) {
      buildEntries(spec.fields, path, entries, {
        mixedObjectBranchPath: path
      });
    }
  }
}

function deriveDefaultGroupsForSchema(schema) {
  if (!(schema && typeof schema === "object")) return [];
  var keys = Object.keys(schema);
  if (keys.length === 0) return [];

  var hasCore = (
    keys.indexOf("name") >= 0 &&
    keys.indexOf("title") >= 0 &&
    keys.indexOf("description") >= 0 &&
    keys.indexOf("type") >= 0
  );
  if (!hasCore) return [];

  return [
    {
      title: "Field Basics",
      description: "Set the field ID/key, title, and user-friendly help text.",
      fields: ["name", "title", "description"]
    },
    {
      title: "Type",
      description: "Choose one or more field types.",
      fields: ["type"]
    },
    {
      title: "Rules",
      description: "Configure required/default and type-specific validation rules.",
      fields: ["required", "default", "of", "list", "min", "max", "step", "pattern"]
    }
  ];
}

function deriveDynamicFieldSchemaGroups(schema, typeCatalog, typeAttributeMap) {
  if (!hasFieldSchemaCore(schema)) return deriveDefaultGroupsForSchema(schema);

  var groups = [
    {
      title: "Field Basics",
      description: "Set the field key, title, and descriptive helper text.",
      fields: ["name", "title", "description"]
    },
    {
      title: "Field Type",
      description: "Choose one or more value types for this field.",
      fields: ["type"]
    },
    {
      title: "Shared Rules",
      description: "Rules that apply across field types.",
      fields: ["required", "default", "list", "fields"]
    }
  ];

  var combinedTypeFields = [];
  var seenFields = {};
  for (var i = 0; i < typeCatalog.length; i++) {
    var typeRow = typeCatalog[i] || {};
    var typeNs = normalizeSingleType(typeRow.namespace || "");
    if (!typeNs) continue;

    var attrs = typeAttributeMap[typeNs] || [];
    for (var a = 0; a < attrs.length; a++) {
      var key = attrs[a];
      if (FIELD_SCHEMA_CORE_KEYS.indexOf(key) >= 0) continue;
      if (key === "required" || key === "default" || key === "list" || key === "fields") continue;
      if (seenFields[key]) continue;
      seenFields[key] = true;
      combinedTypeFields.push(key);
    }
  }

  if (combinedTypeFields.length > 0) {
    groups.push({
      title: "Type Settings",
      description: "Options shown based on the selected field type.",
      fields: combinedTypeFields
    });
  }

  return groups;
}

function buildGroupPlan(entries, groups, options) {
  if (!(groups instanceof Array) || groups.length === 0) return null;
  options = options || {};
  var skipGroupedNestedSteps = options.skipGroupedNestedSteps === true;
  var excludeUngroupedEntries = options.excludeUngroupedEntries === true;

  var byKey = {};
  for (var i = 0; i < entries.length; i++) {
    var entry = entries[i];
    if (entry.path.length === 1) {
      byKey[entry.key] = entry;
    }
  }

  var used = {};
  var plan = [];
  for (var g = 0; g < groups.length; g++) {
    var rawGroup = groups[g] || {};
    var groupFields = rawGroup.fields instanceof Array ? rawGroup.fields : [];
    var groupEntries = [];

    for (var f = 0; f < groupFields.length; f++) {
      var key = (groupFields[f] || "").toString().trim();
      if (!key) continue;
      if (!byKey[key]) continue;
      if (used[key]) continue;
      groupEntries.push(byKey[key]);
      used[key] = true;
    }

    if (groupEntries.length > 0) {
      var groupVisibleForTypes = (rawGroup.visibleForTypes instanceof Array) ? rawGroup.visibleForTypes : [];
      if (groupVisibleForTypes.length > 0) {
        for (var ge = 0; ge < groupEntries.length; ge++) {
          if (!(groupEntries[ge].visibleForTypes instanceof Array) || groupEntries[ge].visibleForTypes.length === 0) {
            groupEntries[ge].visibleForTypes = groupVisibleForTypes.slice();
          }
        }
      }
      plan.push({
        title: safeDisplayText(rawGroup.title, "Step " + (plan.length + 1)),
        description: safeDisplayText(rawGroup.description, ""),
        entries: groupEntries,
        visibleForTypes: groupVisibleForTypes
      });
    }
  }

  if (excludeUngroupedEntries) {
    return plan.length > 0 ? plan : null;
  }

  for (var e = 0; e < entries.length; e++) {
    var left = entries[e];
    if (skipGroupedNestedSteps && left.path.length > 1 && used[left.path[0]]) continue;
    if (left.path.length === 1 && used[left.key]) continue;
    plan.push({
      title: safeDisplayText(left.title, left.key || ("Step " + (plan.length + 1))),
      description: "",
      entries: [left],
      visibleForTypes: []
    });
  }

  return plan.length > 0 ? plan : null;
}

function hasEntryOwnDefault(entry) {
  return !!(entry && entry.spec && Object.prototype.hasOwnProperty.call(entry.spec, "default"));
}

function findAncestorNestedDefaultValue(entry, entries) {
  if (!(entry && entry.path instanceof Array) || !(entries instanceof Array)) return undefined;

  for (var i = entry.path.length - 1; i > 0; i--) {
    var ancestorPath = entry.path.slice(0, i);
    for (var e = 0; e < entries.length; e++) {
      var ancestor = entries[e];
      if (!(ancestor && ancestor.inlineObjectBranch === true && ancestor.path instanceof Array)) continue;
      if (ancestor.path.join(".") !== ancestorPath.join(".")) continue;
      if (!hasEntryOwnDefault(ancestor)) continue;

      var relativePath = getRelativePath(ancestor.path, entry.path);
      var nestedValue = getByPath(ancestor.spec.default, relativePath);
      if (nestedValue !== undefined) {
        return cloneData(nestedValue);
      }
    }
  }

  return undefined;
}

function getEntryDefaultValue(entry, entries) {
  if (hasEntryOwnDefault(entry)) {
    return cloneData(entry.spec.default);
  }
  return findAncestorNestedDefaultValue(entry, entries);
}

function pruneOutputDefaults(output, entries) {
  if (!(output && typeof output === "object") || !(entries instanceof Array) || entries.length === 0) {
    return output;
  }

  var orderedEntries = entries.slice().sort(function(a, b) {
    var aDepth = a && a.path instanceof Array ? a.path.length : 0;
    var bDepth = b && b.path instanceof Array ? b.path.length : 0;
    return aDepth - bDepth;
  });

  for (var i = 0; i < orderedEntries.length; i++) {
    var entry = orderedEntries[i];
    if (!(entry && entry.path instanceof Array) || entry.path.length === 0) continue;

    var defaultValue = getEntryDefaultValue(entry, entries);
    if (defaultValue === undefined) continue;

    var currentValue = getByPath(output, entry.path);
    if (currentValue === undefined) continue;

    if (valuesEqual(currentValue, defaultValue)) {
      deleteByPath(output, entry.path);
    }
  }

  return output;
}

function buildInlineObjectBranchMessage(entry) {
  return {
    n: "div",
    c: "alert alert-info mb-0",
    i: "This object contains nested schema fields. Edit the nested fields in the next steps."
  };
}

function buildEntrySection(entry, compactMode) {
  var hasResetDefault = entry && entry.hasResetDefault === true;
  var section = {
    n: "div",
    c: compactMode ? "mb-3" : "",
    id: entry.navigationTargetId,
    "data-fse-entry-index": String(entry.index),
    "data-fse-entry-path": entry.path.join("."),
    "data-fse-entry-title": safeDisplayText(entry.title, entry.key || ""),
    i: []
  };
  if (entry.visibleForTypes instanceof Array && entry.visibleForTypes.length > 0) {
    section["data-fse-entry-types"] = entry.visibleForTypes.join(",");
  }

  section.i.push({
    n: "div",
    c: "text-muted small mb-1 ua-schema-editor-path",
    i: {
      n: "code",
      i: entry.pathLabel
    }
  });

  if (entry.nestedOnly) {
    var boundaryChildren = [
      {
        n: "div",
        c: "alert alert-info mb-0",
        i: "This object contains nested schema fields. Edit the nested fields in the next steps."
      }
    ];

    if (hasResetDefault) {
      boundaryChildren.push({
        n: "button",
        type: "button",
        c: "btn btn-outline-secondary btn-sm ua-schema-editor-reset-btn mt-2",
        title: "Reset to default",
        "data-fse-reset": String(entry.index),
        "data-fse-reset-type": "object",
        i: [
          { n: "i", c: "bi bi-arrow-counterclockwise me-1", i: "" },
          "Reset nested object"
        ]
      });
    }

    section.i.push({
      n: "div",
      c: "d-flex flex-column align-items-start",
      i: boundaryChildren
    });
    return section;
  }

  if (entry.types.length > 1) {
    section.i.push({
      n: "ua.select.dropdown",
      name: entry.descriptor.typeSelectorName,
      title: "Value Type",
      required: true,
      data: entry.types,
      value: entry.descriptor.initialType,
      description: "Choose which field type to use for this value."
    });
  }

  for (var t = 0; t < entry.types.length; t++) {
    var type = entry.types[t];

    var panelClass = "ua-schema-editor-type-panel";
    if (entry.types.length > 1 && type !== entry.descriptor.initialType) {
      panelClass += " d-none";
    }

    section.i.push({
      n: "div",
      c: panelClass,
      "data-fse-panel": String(entry.index),
      "data-fse-type": type,
      i: (function () {
        if (!hasResetDefault) {
          return entry.descriptor[type];
        }

        return {
          n: "div",
          c: "ua-schema-editor-control-row",
          i: [
            {
              n: "div",
              c: "ua-schema-editor-control-main",
              i: entry.descriptor[type]
            },
            {
              n: "button",
              type: "button",
              c: "btn btn-outline-secondary btn-sm ua-schema-editor-reset-btn",
              title: "Reset to default",
              "data-fse-reset": String(entry.index),
              "data-fse-reset-type": type,
              i: { n: "i", c: "bi bi-arrow-counterclockwise", i: "" }
            }
          ]
        };
      })()
    });
  }

  return section;
}

function prepareControlDescriptor(entry, editorId, initialValue) {
  var descriptor = {
    typeSelectorName: "",
    controlNames: {},
    controlNamespaces: {},
    listStates: {},
    objectDialogs: {},
    baseName: ""
  };

  var baseName = "fse_" + editorId + "_" + entry.index + "_" + encodePath(entry.path);
  descriptor.baseName = baseName;

  if (entry.types.length > 1) {
    descriptor.typeSelectorName = baseName + "__type";
  }

  var initialType = inferTypeFromValue(initialValue, entry.types);

  for (var i = 0; i < entry.types.length; i++) {
    var type = entry.types[i];
    var controlName = baseName + "__" + type;

    descriptor.controlNames[type] = controlName;

    if (type === "list") {
      var itemTypes = normalizeType(entry.spec.of || "string");
      var listState = {
        type: "list",
        hostId: controlName + "__host",
        addButtonId: controlName + "__add",
        itemTypes: itemTypes,
        itemSpec: {
          title: safeDisplayText(((entry.title || entry.key || "") + " Item").trim(), "Item"),
          description: safeDisplayText(entry.description, ""),
          required: false,
          type: itemTypes.length > 1 ? itemTypes : itemTypes[0],
          fields: (entry.spec && entry.spec.fields && typeof entry.spec.fields === "object") ? entry.spec.fields : undefined
        },
        rows: [],
        nextRow: 0,
        hasLiveState: false
      };

      descriptor.controlNamespaces[type] = "ua.fields.schema.editor.list";
      descriptor.listStates[type] = listState;
      descriptor[type] = {
        n: "div",
        i: [
          {
            n: "div",
            id: listState.hostId,
            c: "ua-schema-editor-list-host"
          },
          {
            n: "button",
            id: listState.addButtonId,
            type: "button",
            c: "btn btn-outline-primary btn-sm mt-2",
            i: "Add Item"
          }
        ]
      };
      continue;
    }

    if (type === "object" && entry.spec && entry.spec.dialog === true) {
      descriptor.controlNamespaces[type] = "ua.fields.schema.editor.dialog.object";
      descriptor.controlNames[type] = "";
      descriptor.objectDialogs[type] = {
        title: safeDisplayText(entry.title, entry.key || "Object"),
        description: safeDisplayText(entry.description, ""),
        fields: (entry.spec && entry.spec.fields && typeof entry.spec.fields === "object") ? entry.spec.fields : {},
        value: (type === initialType && initialValue && typeof initialValue === "object") ? initialValue : {}
      };
      descriptor[type] = buildObjectEditorSummaryRow(descriptor.objectDialogs[type].value, descriptor, type);
      continue;
    }

    if (type === "object" && entry.inlineObjectBranch === true && entry.nestedOnly !== true) {
      descriptor.controlNamespaces[type] = "ua.fields.schema.editor.inline.object.branch";
      descriptor.controlNames[type] = "";
      descriptor[type] = buildInlineObjectBranchMessage(entry);
      continue;
    }

    var controlMeta = getControlMetaForType(type, entry.spec);
    descriptor.controlNamespaces[type] = controlMeta.namespace;

    var baseOptions = {
      name: controlName,
      title: safeDisplayText(entry.title, entry.key || ""),
      required: entry.required,
      value: type === initialType ? initialValue : undefined,
      description: safeDisplayText(entry.description, ""),
      stablePath: entry.path.join(".")
    };

    descriptor[type] = stripUndefinedProperties(controlMeta.build(baseOptions));
  }

  descriptor.initialType = initialType;
  descriptor.initialValue = initialValue;
  return descriptor;
}

function getSelectedTypeForEntry(entry, root) {
  if (!entry.descriptor.typeSelectorName) {
    return entry.types[0];
  }

  var selector = root.querySelector("[name='" + entry.descriptor.typeSelectorName + "']");
  return (selector && selector.value) ? selector.value : (entry.descriptor.initialType || entry.types[0]);
}

function findEntryByPath(entries, path) {
  if (!(entries instanceof Array) || !(path instanceof Array)) return null;
  for (var i = 0; i < entries.length; i++) {
    var entry = entries[i];
    if (!(entry && entry.path instanceof Array)) continue;
    if (entry.path.join(".") === path.join(".")) return entry;
  }
  return null;
}

function isMixedInlineObjectChildActive(root, metadata, entry) {
  if (!(entry && entry.mixedObjectBranchPath instanceof Array && entry.mixedObjectBranchPath.length > 0)) {
    return true;
  }
  var parentEntry = findEntryByPath(metadata && metadata.entries, entry.mixedObjectBranchPath);
  if (!parentEntry) return true;
  return normalizeSingleType(getSelectedTypeForEntry(parentEntry, root)) === "object";
}

function syncMixedInlineObjectBranchVisibility(root, metadata) {
  if (!(root && metadata && metadata.entries instanceof Array)) return;

  for (var i = 0; i < metadata.entries.length; i++) {
    var entry = metadata.entries[i];
    if (!(entry && entry.mixedObjectBranchPath instanceof Array && entry.mixedObjectBranchPath.length > 0)) continue;
    var section = root.querySelector("[data-fse-entry-index='" + entry.index + "']");
    if (!section) continue;
    if (isMixedInlineObjectChildActive(root, metadata, entry)) section.classList.remove("d-none");
    else section.classList.add("d-none");
  }

  var steps = root.querySelectorAll("[data-wizard-title]");
  for (var s = 0; s < steps.length; s++) {
    var step = steps[s];
    var entrySections = step.querySelectorAll("[data-fse-entry-index]");
    var stepVisible = entrySections.length === 0;
    for (var e = 0; e < entrySections.length; e++) {
      if (!entrySections[e].classList.contains("d-none")) {
        stepVisible = true;
        break;
      }
    }

    step.setAttribute("data-fse-step-hidden", stepVisible ? "false" : "true");
    var wizardStep = step.closest ? step.closest("[data-wizard-step]") : null;
    if (wizardStep && typeof wizardStep.setAttribute === "function") {
      wizardStep.setAttribute("data-wizard-step-hidden", stepVisible ? "false" : "true");
    }
  }

  try {
    var wizardRoot = root.querySelector("[data-wizard='true']");
    if (wizardRoot && typeof wizardRoot.dispatchEvent === "function") {
      var inlineVisibilityEvent = createDomEvent(wizardRoot, "ua:wizard:visibility-sync", {
        bubbles: false,
        detail: {
          source: "ua.fields.schema.editor.inline-object-branch"
        }
      });
      if (inlineVisibilityEvent) wizardRoot.dispatchEvent(inlineVisibilityEvent);
    }
  } catch (error) {}
}

function getListValueAtPath(source, path, entry) {
  var value = getByPath(source || {}, path);
  if (Array.isArray(value)) return value;

  if (
    entry &&
    entry.spec &&
    Object.prototype.hasOwnProperty.call(entry.spec, "default") &&
    Array.isArray(entry.spec.default)
  ) {
    return cloneData(entry.spec.default);
  }

  return [];
}

function clearEntryListState(root, entry, selectedType) {
  if (!(entry && entry.descriptor && entry.descriptor.listStates)) return;

  var listTypes = Object.keys(entry.descriptor.listStates);
  for (var i = 0; i < listTypes.length; i++) {
    var listType = listTypes[i];
    if (selectedType === listType) continue;

    var listState = entry.descriptor.listStates[listType];
    if (!listState) continue;

    listState.rows = [];
    listState.nextRow = 0;
    listState.hasLiveState = false;

    if (root && root.querySelector) {
      var host = root.querySelector('#' + listState.hostId);
      if (host) host.innerHTML = '';
    }
  }
}

function rebuildEntryBranchCache(entry, value, selectedType) {
  if (!(entry && typeof entry === "object")) return;

  entry.values = {};

  var cacheSeeds = [];
  if (selectedType && value !== undefined) {
    cacheSeeds.push({
      type: selectedType,
      value: value,
      overwrite: true
    });
  }

  var defaultValue = getEntryDefaultValue(entry, entry && entry.stateEntries instanceof Array ? entry.stateEntries : null);
  if (defaultValue !== undefined) {
    cacheSeeds.push({
      type: inferTypeFromValue(defaultValue, entry.types || []),
      value: defaultValue,
      overwrite: false
    });
  }

  for (var i = 0; i < cacheSeeds.length; i++) {
    var seed = cacheSeeds[i];
    if (!(seed && seed.type)) continue;
    if (seed.overwrite !== true && Object.prototype.hasOwnProperty.call(entry.values, seed.type)) continue;
    entry.values[seed.type] = cloneData(seed.value);
  }
}

function clearInactiveEntryState(root, entry, activeType, metadata) {
  if (!(entry && entry.descriptor)) return;

  clearEntryListState(root, entry, activeType);

  var loadPatch = {};

  if (entry.descriptor.objectDialogs) {
    var dialogTypes = Object.keys(entry.descriptor.objectDialogs);
    for (var i = 0; i < dialogTypes.length; i++) {
      var dialogType = dialogTypes[i];
      if (dialogType === activeType) continue;
      entry.descriptor.objectDialogs[dialogType].value = {};
      updateObjectEntrySummary(root, entry.descriptor, dialogType, {});
    }
  }

  var controlTypes = Object.keys(entry.descriptor.controlNames || {});
  for (var controlIndex = 0; controlIndex < controlTypes.length; controlIndex++) {
    var controlType = controlTypes[controlIndex];
    if (controlType === activeType || controlType === "list") continue;
    if (entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[controlType]) continue;

    var controlName = entry.descriptor.controlNames[controlType];
    if (!controlName) continue;
    loadPatch[controlName] = controlType === "object" ? {} : "";
  }

  if (metadata && Object.keys(loadPatch).length > 0) {
    return window.UATools.load(loadPatch, "#" + metadata.wizardDomId);
  }
}

async function applyNestedObjectBoundaryValue(root, metadata, entry, value) {
  if (!(root && metadata && entry && entry.path instanceof Array)) return;

  var nextValue = (value && typeof value === "object" && !Array.isArray(value)) ? value : {};
  for (var i = 0; i < metadata.entries.length; i++) {
    var childEntry = metadata.entries[i];
    if (!(childEntry && childEntry.path instanceof Array)) continue;
    if (childEntry.path.length <= entry.path.length) continue;
    if (childEntry.path.slice(0, entry.path.length).join(".") !== entry.path.join(".")) continue;
    if (childEntry.nestedOnly) continue;

    var childRelativePath = getRelativePath(entry.path, childEntry.path);
    await applyEntryValue(root, metadata, childEntry, getByPath(nextValue, childRelativePath));
  }
}

async function applyEntryValue(root, metadata, entry, value) {
  if (!(root && metadata && entry && entry.descriptor)) return;

  if (entry.nestedOnly) {
    await applyNestedObjectBoundaryValue(root, metadata, entry, value);
    return;
  }

  var selectedType = entry.types[0];
  if (entry.types.length > 1) {
    if (value !== undefined) {
      selectedType = inferTypeFromValue(value, entry.types);
    } else if (entry.descriptor.typeSelectorName) {
      selectedType = getSelectedTypeForEntry(entry, root);
    }
  }

  await clearInactiveEntryState(root, entry, selectedType, metadata);

  var loadObject = {};
  if (entry.descriptor.typeSelectorName) {
    loadObject[entry.descriptor.typeSelectorName] = selectedType;
  }

  if (selectedType === "list") {
    var listState = entry.descriptor.listStates && entry.descriptor.listStates[selectedType];
    if (!listState) return;

    var host = root.querySelector('#' + listState.hostId);
    if (host) host.innerHTML = '';
    listState.rows = [];
    listState.nextRow = 0;

    if (Object.keys(loadObject).length > 0) {
      await window.UATools.load(loadObject, "#" + metadata.wizardDomId);
    }

    var listItems = Array.isArray(value) ? value : [];
    for (var li = 0; li < listItems.length; li++) {
      await addListRow(root, entry, listState, cloneData(listItems[li]));
    }
    return;
  }

  if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
    if (Object.keys(loadObject).length > 0) {
      await window.UATools.load(loadObject, "#" + metadata.wizardDomId);
    }
    entry.descriptor.objectDialogs[selectedType].value = (value && typeof value === "object" && !Array.isArray(value))
      ? cloneData(value)
      : {};
    updateObjectEntrySummary(root, entry.descriptor, selectedType, entry.descriptor.objectDialogs[selectedType].value);
    return;
  }

  if (selectedType === "object" && entry.inlineObjectBranch === true && entry.nestedOnly !== true) {
    if (Object.keys(loadObject).length > 0) {
      await window.UATools.load(loadObject, "#" + metadata.wizardDomId);
    }
    await applyNestedObjectBoundaryValue(root, metadata, entry, value);
    syncMixedInlineObjectBranchVisibility(root, metadata);
    return;
  }

  var controlName = entry.descriptor.controlNames[selectedType] || entry.descriptor.controlNames[entry.types[0]];
  if (controlName) {
    loadObject[controlName] = (value === undefined) ? "" : value;
  }

  if (Object.keys(loadObject).length > 0) {
    await window.UATools.load(loadObject, "#" + metadata.wizardDomId);
  }
}

function getInlineObjectControlType(entry) {
  if (!(entry && entry.descriptor && entry.types instanceof Array)) return "";

  for (var i = 0; i < entry.types.length; i++) {
    var type = entry.types[i];
    if (normalizeSingleType(type) !== "object") continue;
    if (
      entry.descriptor.controlNamespaces[type] !== "ua.fields.schema.editor" &&
      entry.descriptor.controlNamespaces[type] !== "ua.fields.schema.editor.inline.object.branch"
    ) continue;
    return type;
  }

  return "";
}

function getDetachedObjectControlType(entry) {
  if (!(entry && entry.descriptor && entry.types instanceof Array)) return "";

  for (var i = 0; i < entry.types.length; i++) {
    var type = entry.types[i];
    if (normalizeSingleType(type) !== "object") continue;
    if (!(entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[type])) continue;
    return type;
  }

  return "";
}

function runAfterRenderHooks() {
  if (typeof window === "undefined" || !window.UATools) {
    return;
  }

  if (typeof window.UATools.after === "function") {
    window.UATools.after();
  }
}

function createDomEvent(root, eventName, options) {
  if (!eventName) return null;

  var eventOptions = (options && typeof options === "object") ? options : {};
  var doc = root && root.ownerDocument ? root.ownerDocument : (typeof document !== "undefined" ? document : null);
  var view = doc && doc.defaultView ? doc.defaultView : null;
  var constructor = eventOptions.detail !== undefined
    ? (view && typeof view.CustomEvent === "function" ? view.CustomEvent : (typeof CustomEvent === "function" ? CustomEvent : null))
    : (view && typeof view.Event === "function" ? view.Event : (typeof Event === "function" ? Event : null));

  if (typeof constructor !== "function") {
    return null;
  }

  try {
    return new constructor(eventName, eventOptions);
  } catch (error) {
    return null;
  }
}

function notifyEditorMutation(root, detail) {
  if (!root || typeof root.dispatchEvent !== "function") return;

  var payload = (detail && typeof detail === "object") ? detail : {};

  try {
    var mutationEvent = createDomEvent(root, "ua:schema-editor:mutated", {
      bubbles: true,
      detail: payload
    });
    if (mutationEvent) root.dispatchEvent(mutationEvent);
  } catch (error) {}

  try {
    var inputEvent = createDomEvent(root, "input", { bubbles: true });
    if (inputEvent) root.dispatchEvent(inputEvent);
  } catch (error) {}

  try {
    var changeEvent = createDomEvent(root, "change", { bubbles: true });
    if (changeEvent) root.dispatchEvent(changeEvent);
  } catch (error) {}
}

function isObjectLikeEditorType(type) {
  var normalizedType = normalizeSingleType(type);
  return normalizedType === "object" || type === FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE;
}

function isPendingBlankListValue(value, type) {
  if (isObjectLikeEditorType(type)) {
    return value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0;
  }

  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

function isEmptyObjectValue(value) {
  return !!(value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);
}

function summarizeObjectValue(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "Not configured";
  }

  var keys = Object.keys(value);
  if (keys.length === 0) {
    return "Not configured";
  }

  return keys.length + " field" + (keys.length === 1 ? "" : "s") + " configured";
}

function normalizeStableEditorToken(value) {
  return (value || "").toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function buildStableEditorSeed(options) {
  var opts = options && typeof options === "object" && !(options instanceof Array) ? options : {};
  var candidates = [
    opts.name,
    opts.rootEditorName,
    opts.label,
    opts.title,
    opts.rootEditorLabel
  ];
  var disambiguators = [
    opts.stableKey,
    opts.stablePath,
    opts.editorKey,
    opts.editorPath,
    opts.navigationKey,
    opts.navigationPath
  ];
  var baseToken = "";

  for (var i = 0; i < candidates.length; i++) {
    baseToken = normalizeStableEditorToken(candidates[i]);
    if (baseToken) break;
  }

  if (!baseToken) return "";

  for (var d = 0; d < disambiguators.length; d++) {
    var disambiguatorToken = normalizeStableEditorToken(disambiguators[d]);
    if (!disambiguatorToken) continue;
    return baseToken + "_" + disambiguatorToken;
  }

  return "";
}

function buildObjectEditorSummaryRow(summaryValue, descriptor, objectType) {
  return {
    n: "div",
    c: "d-flex align-items-center justify-content-between gap-2 flex-wrap",
    i: [
      {
        n: "div",
        c: "text-muted small",
        "data-fse-object-summary": descriptor.baseName,
        "data-fse-object-summary-type": objectType,
        i: summarizeObjectValue(summaryValue)
      },
      {
        n: "button",
        type: "button",
        c: "btn btn-outline-primary btn-sm",
        "data-fse-object-edit": descriptor.baseName,
        "data-fse-object-edit-type": objectType,
        i: [
          { n: "i", c: "bi bi-pencil-square me-1", i: "" },
          "Edit"
        ]
      }
    ]
  };
}

function updateObjectEntrySummary(root, descriptor, objectType, value) {
  var summary = root.querySelector("[data-fse-object-summary='" + descriptor.baseName + "'][data-fse-object-summary-type='" + objectType + "']");
  if (!summary) return;
  summary.textContent = summarizeObjectValue(value);
}

function updateObjectRowSummary(root, rowBase, rowType, value) {
  var summary = root.querySelector("[data-fse-list-summary='" + rowBase + "'][data-fse-list-summary-type='" + rowType + "']");
  if (!summary) return;
  summary.textContent = summarizeObjectValue(value);
}

function getEntrySelectedType(root, entry) {
  if (!(entry && entry.types instanceof Array && entry.types.length > 0)) return "";
  if (!entry.descriptor || !entry.descriptor.typeSelectorName) return entry.types[0];
  var selector = root && root.querySelector ? root.querySelector("[name='" + entry.descriptor.typeSelectorName + "']") : null;
  return (selector && selector.value) ? selector.value : entry.types[0];
}

async function getCurrentEntryBranchValue(root, metadata, entry, selectedType) {
  if (!(root && entry && entry.descriptor && selectedType)) return undefined;

  if (selectedType === "list") {
    return undefined;
  }

  if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
    return cloneData(entry.descriptor.objectDialogs[selectedType].value || {});
  }

  var controlName = entry.descriptor.controlNames[selectedType] || entry.descriptor.controlNames[entry.types[0]];
  if (!controlName) return undefined;

  if (entry.descriptor.controlNamespaces && entry.descriptor.controlNamespaces[selectedType] === "ua.fields.schema.editor") {
    var saveResult = await window.UATools.save("[name='" + controlName + "']", true);
    var controlSave = saveResult && saveResult[controlName];
    if (!controlSave || controlSave.success === false) return undefined;
    return cloneData(controlSave.data);
  }

  return cloneData(readControlValueFromDom(root, controlName));
}

async function applyEntryBranchValue(root, metadata, entry, selectedType, value) {
  if (!(root && metadata && entry && entry.descriptor && selectedType)) return;

  if (selectedType === "list") return;

  if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
    entry.descriptor.objectDialogs[selectedType].value = (value && typeof value === "object" && !Array.isArray(value)) ? cloneData(value) : {};
    updateObjectEntrySummary(root, entry.descriptor, selectedType, entry.descriptor.objectDialogs[selectedType].value);
    return;
  }

  var controlName = entry.descriptor.controlNames[selectedType] || entry.descriptor.controlNames[entry.types[0]];
  if (!controlName) return;

  var loadPatch = {};
  loadPatch[controlName] = value === undefined ? "" : cloneData(value);
  await window.UATools.load(loadPatch, "#" + metadata.wizardDomId);
}

function getRowSelectedType(root, rowState) {
  if (!(rowState && rowState.rowTypes instanceof Array && rowState.rowTypes.length > 0)) return "";
  if (!rowState.rowTypeName) return rowState.rowTypes[0];
  var selector = root && root.querySelector ? root.querySelector("[name='" + rowState.rowTypeName + "']") : null;
  return (selector && selector.value) ? selector.value : rowState.rowTypes[0];
}

function readControlValueFromDom(root, controlName) {
  if (!(root && root.querySelector && controlName)) return undefined;
  var control = root.querySelector("[name='" + controlName + "']");
  if (!control) return undefined;

  if (control.type === "checkbox") {
    return !!control.checked;
  }

  if ("value" in control) {
    return control.value;
  }

  var fieldTypeChecks = control.querySelectorAll("input[data-field-type-option='true']:checked");
  if (fieldTypeChecks.length > 0) {
    var selectedFieldTypes = [];
    for (var f = 0; f < fieldTypeChecks.length; f++) {
      var fieldTypeValue = normalizeSingleType(fieldTypeChecks[f].value || "");
      if (!fieldTypeValue) continue;
      if (selectedFieldTypes.indexOf(fieldTypeValue) >= 0) continue;
      selectedFieldTypes.push(fieldTypeValue);
    }

    if (selectedFieldTypes.length <= 1) {
      return selectedFieldTypes[0] || "";
    }

    return selectedFieldTypes;
  }

  var multiSelectChecks = control.querySelectorAll("input[data-multi-check='true']:checked");
  if (multiSelectChecks.length > 0) {
    var selectedOptions = [];
    for (var m = 0; m < multiSelectChecks.length; m++) {
      var selectedValue = safeDisplayText(multiSelectChecks[m].value, "").trim();
      if (!selectedValue) continue;
      if (selectedOptions.indexOf(selectedValue) >= 0) continue;
      selectedOptions.push(selectedValue);
    }

    if (selectedOptions.length > 0) return selectedOptions;
  }

  return undefined;
}

function getCurrentListRowValue(root, rowState) {
  if (!(rowState && rowState.controlNames)) return undefined;
  var selectedType = getRowSelectedType(root, rowState);
  if (!selectedType) return undefined;

  if (rowState.values && Object.prototype.hasOwnProperty.call(rowState.values, selectedType)) {
    return rowState.values[selectedType];
  }

  var controlName = rowState.controlNames[selectedType];
  if (!controlName) return undefined;

  var currentValue = readControlValueFromDom(root, controlName);
  if (currentValue === undefined) return undefined;

  if (!rowState.values || typeof rowState.values !== "object") {
    rowState.values = {};
  }
  rowState.values[selectedType] = currentValue;
  return currentValue;
}

async function cacheListRowTypeValue(root, rowState, rowType) {
  if (!(rowState && rowState.controlNames && rowType)) return;

  var controlName = rowState.controlNames[rowType];
  if (!controlName) return;

  var controlNamespace = rowState.controlNamespaces && rowState.controlNamespaces[rowType];
  if (controlNamespace === "ua.fields.schema.editor") {
    if (!(window && window.UATools && typeof window.UATools.save === "function")) return;
    var saveResult = await window.UATools.save("[name='" + controlName + "']", true);
    if (!saveResult || saveResult.success === false) return;
    var controlSave = saveResult[controlName];
    if (!controlSave || controlSave.success === false) return;
    if (!rowState.values || typeof rowState.values !== "object") {
      rowState.values = {};
    }
    rowState.values[rowType] = controlSave.data;
    return;
  }

  getCurrentListRowValue(root, rowState);
}

async function applyListRowTypeSelection(root, rowDom, rowBase, rowState, selectedType) {
  if (!(rowDom && rowState)) return;

  var panels = rowDom.querySelectorAll("[data-fse-list-row-panel='" + rowBase + "']");
  for (var j = 0; j < panels.length; j++) {
    var panel = panels[j];
    var panelType = panel.getAttribute("data-fse-list-row-type");
    if (panelType === selectedType) panel.classList.remove("d-none");
    else panel.classList.add("d-none");
  }

  if (!(rowState.values && Object.prototype.hasOwnProperty.call(rowState.values, selectedType))) {
    return;
  }

  if (normalizeSingleType(selectedType) === "object") {
    updateObjectRowSummary(root, rowBase, selectedType, rowState.values[selectedType]);
    return;
  }

  var controlName = rowState.controlNames[selectedType];
  if (!controlName) return;

  var loadPatch = {};
  loadPatch[controlName] = rowState.values[selectedType];
  await window.UATools.load(loadPatch);
}

function refreshListRowState(root, entry, listState) {
  if (!(entry && entry.path instanceof Array) || !(listState && listState.rows instanceof Array)) return;

  for (var i = 0; i < listState.rows.length; i++) {
    var rowState = listState.rows[i];
    if (!rowState) continue;

    var rowPath = entry.path.concat([String(i)]);
    rowState.rowPath = rowPath;

    var rowEl = root && root.querySelector
      ? root.querySelector("[data-fse-list-row='" + rowState.rowBase + "']")
      : null;
    if (rowEl && typeof rowEl.setAttribute === "function") {
      rowEl.setAttribute("data-fse-row-path", rowPath.join("."));
    }
  }
}

function isFieldItemSpec(listState) {
  if (!listState || !listState.itemSpec || typeof listState.itemSpec !== "object") return false;
  var fields = listState.itemSpec.fields;
  if (!(fields && typeof fields === "object")) return false;
  return (
    Object.prototype.hasOwnProperty.call(fields, "name") &&
    Object.prototype.hasOwnProperty.call(fields, "title") &&
    Object.prototype.hasOwnProperty.call(fields, "description") &&
    Object.prototype.hasOwnProperty.call(fields, "type")
  );
}

function formatFieldEditorContext(value, isEditMode, listState) {
  var safeValue = (value && typeof value === "object") ? value : {};
  var isFieldEditor = isFieldItemSpec(listState);
  var defaultItemTitle = safeDisplayText((listState && listState.itemSpec && listState.itemSpec.title) || "Item", "Item");
  if (!isFieldEditor) {
    return {
      title: defaultItemTitle + " Editor",
      subtitle: isEditMode ? "Editing item details." : "Adding a new item."
    };
  }

  var fieldTitle = safeDisplayText(safeValue.title, "");
  var fieldName = safeDisplayText(safeValue.name, "");

  if (!isEditMode) {
    return {
      title: "Add New Field",
      subtitle: "Adding a new field."
    };
  }

  if (!fieldTitle) fieldTitle = "Untitled Field";

  var subtitle = "Editing field: " + fieldTitle;
  if (fieldName) {
    subtitle += " (." + fieldName + ")";
  }

  return {
    title: "Edit Field",
    subtitle: subtitle
  };
}

function createDetachedDialogTeardown(root, entry, dialogDom, onFinalize, onCleanup) {
  var finalized = false;
  var watchTimer = null;
  var hiddenListener = null;
  var reconcileResult = false;

  function clearWatchTimer() {
    if (watchTimer) clearInterval(watchTimer);
    watchTimer = null;
  }

  function disposeBootstrapModal() {
    if (!(dialogDom) || !(window.bootstrap && window.bootstrap.Modal && typeof window.bootstrap.Modal.getInstance === "function")) return;
    var modalInstance = window.bootstrap.Modal.getInstance(dialogDom);
    if (modalInstance && typeof modalInstance.dispose === "function") {
      modalInstance.dispose();
    }
  }

  function removeDialogDom() {
    if (hiddenListener && dialogDom && typeof dialogDom.removeEventListener === "function") {
      dialogDom.removeEventListener("hidden.bs.modal", hiddenListener);
      hiddenListener = null;
    }
    disposeBootstrapModal();
    if (dialogDom && dialogDom.parentNode) {
      dialogDom.parentNode.removeChild(dialogDom);
    }
  }

  function finalize(result) {
    if (finalized) return false;
    finalized = true;
    clearWatchTimer();
    if (typeof onCleanup === "function") {
      try {
        onCleanup();
      } catch (error) {}
    }
    if (typeof onFinalize === "function") onFinalize(result);
    return true;
  }

  function completeClose(result) {
    var changed = finalize(result);
    if (!(changed)) return false;
    removeDialogDom();
    runAfterRenderHooks();
    emitNavigationState(root, root && root.__uaSchemaEditor, entry, { kind: "dialog-close" });
    return true;
  }

  function close(result) {
    return completeClose(result);
  }

  function reconcile(result) {
    if (!(dialogDom)) return finalize(result);
    var removed = !document.body.contains(dialogDom);
    var hidden = dialogDom.hasAttribute("hidden") || dialogDom.classList.contains("d-none") || dialogDom.style.display === "none";
    if (!(removed || hidden)) return false;
    return completeClose(result);
  }

  if (dialogDom && typeof dialogDom.addEventListener === "function") {
    hiddenListener = function() {
      reconcile(reconcileResult);
    };
    dialogDom.addEventListener("hidden.bs.modal", hiddenListener);
  }

  return {
    finalize: finalize,
    close: close,
    reconcile: reconcile,
    setWatchTimer: function(nextTimer) {
      clearWatchTimer();
      watchTimer = nextTimer || null;
      return watchTimer;
    },
    setReconcileResult: function(result) {
      reconcileResult = result;
      return reconcileResult;
    },
    clearWatchTimer: clearWatchTimer,
    isFinalized: function() {
      return finalized;
    }
  };
}

async function mountDetachedSchemaEditor(dialogDom, mountId, renderOptions) {
  if (!(dialogDom && mountId)) return null;

  var mount = dialogDom.querySelector("#" + mountId);
  if (!mount) return null;

  var editorOptions = (renderOptions && typeof renderOptions === "object") ? renderOptions : {};
  var editorDom = await exports.render(editorOptions);
  mount.innerHTML = "";
  mount.appendChild(editorDom);
  if (typeof exports.load === "function") {
    await exports.load(editorOptions.name || "", editorDom, editorOptions.data);
  }
  runAfterRenderHooks();
  return editorDom;
}

async function openObjectListEditor(root, entry, rowBase, rowType, rowControlName, rowState, listState, initialValue) {
  var dialogId = rowBase + "__dialog__" + rowType;
  var currentValue = initialValue;

  if (!currentValue && rowState && rowState.values && typeof rowState.values[rowType] === "object") {
    currentValue = rowState.values[rowType];
  }
  var editorCopy = (currentValue && typeof currentValue === "object") ? currentValue : {};
  var editorContext = formatFieldEditorContext(editorCopy, true, listState);
  var rootEditorMeta = root && root.__uaSchemaEditor ? root.__uaSchemaEditor : {};
  var editorMountId = dialogId + "__editor_mount";
  var detachedErrorId = dialogId + "__error";
  var detachedEditorOptions = {
    name: rowControlName,
    schema: (listState.itemSpec.fields && typeof listState.itemSpec.fields === "object") ? listState.itemSpec.fields : {},
    groups: deriveDynamicFieldSchemaGroups(
      (listState.itemSpec.fields && typeof listState.itemSpec.fields === "object") ? listState.itemSpec.fields : {},
      buildFieldCatalog(),
      buildFieldAttributeMap()
    ),
    data: (currentValue && typeof currentValue === "object") ? currentValue : {},
    submitText: "Update Field",
    navigationContextDetached: true,
    navigationContextSegments: buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
      {
        key: String(rowState && rowState.rowBase || "item"),
        label: getBestListRowLabel(editorCopy, rowState && rowState.rowPath ? rowState.rowPath[rowState.rowPath.length - 1] : 0, listState, entry),
        path: rowState && rowState.rowPath instanceof Array ? rowState.rowPath.slice() : [],
        pathLabel: rowState && rowState.rowPath instanceof Array && rowState.rowPath.length ? ("." + rowState.rowPath.join(".")) : "",
        navigationTargetId: rowState && rowState.navigationTargetId ? rowState.navigationTargetId : ""
      }
    ]),
    navigationContextSegmentsResolver: function(detachedMetadata, detachedRoot) {
      var currentDialogValue = collectLiveEditorData(detachedRoot, detachedMetadata);
      return buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
        {
          key: String(rowState && rowState.rowBase || "item"),
          label: getBestListRowLabel(currentDialogValue, rowState && rowState.rowPath ? rowState.rowPath[rowState.rowPath.length - 1] : 0, listState, entry),
          path: rowState && rowState.rowPath instanceof Array ? rowState.rowPath.slice() : [],
          pathLabel: rowState && rowState.rowPath instanceof Array && rowState.rowPath.length ? ("." + rowState.rowPath.join(".")) : "",
          navigationTargetId: rowState && rowState.navigationTargetId ? rowState.navigationTargetId : ""
        }
      ]);
    },
    stablePath: (rowState && rowState.rowPath instanceof Array ? rowState.rowPath.join(".") : "") + ".dialog." + rowType,
    rootEditorName: rootEditorMeta.rootEditorName ? rootEditorMeta.rootEditorName : (rootEditorMeta.editorName || ""),
    rootEditorLabel: rootEditorMeta.rootEditorLabel ? rootEditorMeta.rootEditorLabel : (rootEditorMeta.editorLabel || "")
  };

  var cancelButtonId = dialogId + "__cancel";
  var dialogDoc = {
    n: "ua.dialog",
    id: dialogId,
    title: editorContext.title,
    breadcrumbTitle: "Field Details",
    allowclose: true,
    fullscreen: true,
    breadcrumb: true,
    lockParent: true,
    inner: [
      {
        n: "p",
        c: "text-muted mb-2",
        i: editorContext.subtitle
      },
      {
        n: "div",
        id: detachedErrorId,
        c: "alert alert-danger d-none mt-3 mb-0",
        role: "alert"
      },
      {
        n: "div",
        id: editorMountId
      }
    ],
    footer: {
      n: "div",
      c: "col-12 d-flex justify-content-end gap-2",
      i: [
        {
          n: "button",
          type: "button",
          id: cancelButtonId,
          c: "btn btn-outline-secondary",
          "data-ua-dismiss": "modal",
          i: "Cancel"
        },
        {
          n: "button",
          type: "button",
          id: dialogId + "__back",
          c: "btn btn-outline-secondary d-none",
          i: "Back"
        },
        {
          n: "button",
          type: "button",
          id: dialogId + "__save",
          c: "btn btn-primary",
          i: "Continue"
        }
      ]
    }
  };

  var dialogDom = await window.UATools.render(dialogDoc);
  document.body.appendChild(dialogDom);
  await mountDetachedSchemaEditor(dialogDom, editorMountId, detachedEditorOptions);
  runAfterRenderHooks();

  var detachedEditorRoot = findSchemaEditorRoot(dialogDom);
  attachDetachedNavigationContextResolver(detachedEditorRoot, function() {
    var detachedState = detachedEditorRoot && detachedEditorRoot.__uaSchemaEditor ? detachedEditorRoot.__uaSchemaEditor : null;
    var currentDialogValue = collectLiveEditorData(detachedEditorRoot, detachedState);
    return buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
      {
        key: String(rowState && rowState.rowBase || "item"),
        label: getBestListRowLabel(currentDialogValue, rowState && rowState.rowPath ? rowState.rowPath[rowState.rowPath.length - 1] : 0, listState, entry),
        path: rowState && rowState.rowPath instanceof Array ? rowState.rowPath.slice() : [],
        pathLabel: rowState && rowState.rowPath instanceof Array && rowState.rowPath.length ? ("." + rowState.rowPath.join(".")) : "",
        navigationTargetId: rowState && rowState.navigationTargetId ? rowState.navigationTargetId : ""
      }
    ]);
  });

  var saveButton = dialogDom.querySelector("#" + dialogId + "__save");
  if (!saveButton) return false;
  var backButton = dialogDom.querySelector("#" + dialogId + "__back");
  var cancelButton = dialogDom.querySelector("#" + cancelButtonId);

  return await new Promise(function(resolve) {
    var detachedErrorRecoveryBinding = bindDetachedDialogErrorRecovery(dialogDom, detachedErrorId);
    var detachedWizardBinding = bindDetachedWizardDialogActions(dialogDom, {
      backButton: backButton,
      primaryButton: saveButton,
      onFinalSubmit: async function() {
        clearDetachedDialogError(dialogDom, detachedErrorId);
        var saveResult = await saveDetachedSchemaEditor(detachedEditorRoot, "#" + dialogId, rowControlName);
        if (!saveResult || saveResult.success === false) {
          showDetachedDialogError(dialogDom, detachedErrorId, saveResult);
          return;
        }

        if (!rowState.values || typeof rowState.values !== "object") {
          rowState.values = {};
        }
        rowState.values[rowType] = saveResult.data || {};
        updateObjectRowSummary(root, rowBase, rowType, rowState.values[rowType]);
        notifyEditorMutation(root, {
          kind: "object-list-edit",
          rowBase: rowBase,
          rowType: rowType
        });

        dialogTeardown.close(true);
      }
    });

    var dialogTeardown = createDetachedDialogTeardown(root, entry, dialogDom, function(result) {
      resolve(!!result);
    }, function() {
      if (detachedErrorRecoveryBinding && typeof detachedErrorRecoveryBinding.disconnect === "function") {
        detachedErrorRecoveryBinding.disconnect();
      }
      if (detachedWizardBinding && typeof detachedWizardBinding.disconnect === "function") {
        detachedWizardBinding.disconnect();
      }
    });

    if (cancelButton) {
      cancelButton.addEventListener("click", function () {
        dialogTeardown.close(false);
      });
    }

    dialogTeardown.setReconcileResult(false);
    dialogTeardown.setWatchTimer(setInterval(function() {
      if (dialogTeardown.isFinalized()) return;
      dialogTeardown.reconcile(false);
    }, 150));
  });
}

async function openObjectEntryEditor(root, entry, objectType) {
  if (!(entry && entry.descriptor && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[objectType])) return false;

  var dialogMeta = entry.descriptor.objectDialogs[objectType];
  var dialogId = entry.descriptor.baseName + "__dialog__" + objectType;
  var controlName = dialogId + "__value";
  var currentValue = (dialogMeta.value && typeof dialogMeta.value === "object") ? dialogMeta.value : {};
  var rootEditorMeta = root && root.__uaSchemaEditor ? root.__uaSchemaEditor : {};
  var editorMountId = dialogId + "__editor_mount";
  var detachedErrorId = dialogId + "__error";
  var detachedEditorOptions = {
    name: controlName,
    schema: dialogMeta.fields || {},
    data: currentValue,
    showSummaryStep: false,
    skipGroupedNestedSteps: true,
    navigationContextDetached: true,
    navigationContextSegments: buildNavigationPathFromEntry(entry, rootEditorMeta),
    navigationContextSegmentsResolver: function() {
      return buildNavigationPathFromEntry(entry, rootEditorMeta);
    },
    stablePath: entry.path.join(".") + ".dialog." + objectType,
    rootEditorName: rootEditorMeta.rootEditorName ? rootEditorMeta.rootEditorName : (rootEditorMeta.editorName || ""),
    rootEditorLabel: rootEditorMeta.rootEditorLabel ? rootEditorMeta.rootEditorLabel : (rootEditorMeta.editorLabel || "")
  };

  var dialogDoc = {
    n: "ua.dialog",
    id: dialogId,
    title: dialogMeta.title || "Edit Object",
    breadcrumbTitle: "Property Details",
    allowclose: true,
    fullscreen: true,
    breadcrumb: true,
    lockParent: true,
    inner: [
      dialogMeta.description ? {
        n: "p",
        c: "text-muted mb-2",
        i: dialogMeta.description
      } : "",
      {
        n: "div",
        id: detachedErrorId,
        c: "alert alert-danger d-none mt-3 mb-0",
        role: "alert"
      },
      {
        n: "div",
        id: editorMountId
      }
    ],
    footer: {
      n: "div",
      c: "col-12 d-flex justify-content-end gap-2",
      i: [
        {
          n: "button",
          type: "button",
          id: dialogId + "__cancel",
          c: "btn btn-outline-secondary",
          "data-ua-dismiss": "modal",
          i: "Cancel"
        },
        {
          n: "button",
          type: "button",
          id: dialogId + "__save",
          c: "btn btn-primary",
          i: "Save"
        }
      ]
    }
  };

  var dialogDom = await window.UATools.render(dialogDoc);
  document.body.appendChild(dialogDom);
  await mountDetachedSchemaEditor(dialogDom, editorMountId, detachedEditorOptions);
  runAfterRenderHooks();

  var detachedEditorRoot = findSchemaEditorRoot(dialogDom);
  attachDetachedNavigationContextResolver(detachedEditorRoot, function() {
    return buildNavigationPathFromEntry(entry, rootEditorMeta);
  });

  return await new Promise(function(resolve) {
    var detachedErrorRecoveryBinding = bindDetachedDialogErrorRecovery(dialogDom, detachedErrorId);
    var dialogTeardown = createDetachedDialogTeardown(root, entry, dialogDom, function(result) {
      resolve(!!result);
    }, function() {
      if (detachedErrorRecoveryBinding && typeof detachedErrorRecoveryBinding.disconnect === "function") {
        detachedErrorRecoveryBinding.disconnect();
      }
    });

    var saveButton = dialogDom.querySelector("#" + dialogId + "__save");
    var cancelButton = dialogDom.querySelector("#" + dialogId + "__cancel");

    if (saveButton) {
      saveButton.addEventListener("click", async function() {
        clearDetachedDialogError(dialogDom, detachedErrorId);
        var saveResult = await saveDetachedSchemaEditor(detachedEditorRoot, "#" + dialogId, controlName);
        if (!saveResult || saveResult.success === false) {
          showDetachedDialogError(dialogDom, detachedErrorId, saveResult);
          return;
        }
        dialogMeta.value = saveResult.data || {};
        updateObjectEntrySummary(root, entry.descriptor, objectType, dialogMeta.value);
        notifyEditorMutation(root, {
          kind: "object-entry-edit",
          path: entry.path,
          objectType: objectType
        });
        dialogTeardown.close(true);
      });
    }

    if (cancelButton) {
      cancelButton.addEventListener("click", function() {
        dialogTeardown.close(false);
      });
    }

    dialogTeardown.setReconcileResult(false);
    dialogTeardown.setWatchTimer(setInterval(function() {
      if (dialogTeardown.isFinalized()) return;
      dialogTeardown.reconcile(false);
    }, 150));
  });
}

async function collectObjectListValue(root, entry, listState, initialValue) {
  var dialogId = "fse_obj_new_" + uai.generateRandomID(10);
  var controlName = dialogId + "__value";
  var currentValue = (initialValue && typeof initialValue === "object") ? initialValue : {};
  var editorContext = formatFieldEditorContext(currentValue, false, listState);
  var rootEditorMeta = root && root.__uaSchemaEditor ? root.__uaSchemaEditor : {};
  var pendingRowIndex = listState && listState.rows instanceof Array ? listState.rows.length : 0;
  var pendingRowPath = entry && entry.path instanceof Array ? entry.path.concat([String(pendingRowIndex)]) : [];
  var editorMountId = dialogId + "__editor_mount";
  var detachedErrorId = dialogId + "__error";
  var detachedEditorOptions = {
    name: controlName,
    schema: (listState.itemSpec.fields && typeof listState.itemSpec.fields === "object") ? listState.itemSpec.fields : {},
    groups: deriveDynamicFieldSchemaGroups(
      (listState.itemSpec.fields && typeof listState.itemSpec.fields === "object") ? listState.itemSpec.fields : {},
      buildFieldCatalog(),
      buildFieldAttributeMap()
    ),
    data: currentValue,
    submitText: "Add Field",
    navigationContextDetached: true,
    navigationContextSegments: buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
      {
        key: String(pendingRowIndex),
        label: getBestListRowLabel(currentValue, pendingRowIndex, listState, entry),
        path: pendingRowPath,
        pathLabel: pendingRowPath.length ? ("." + pendingRowPath.join(".")) : "",
        navigationTargetId: ""
      }
    ]),
    navigationContextSegmentsResolver: function(detachedMetadata, detachedRoot) {
      var currentDialogValue = collectLiveEditorData(detachedRoot, detachedMetadata);
      return buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
        {
          key: String(pendingRowIndex),
          label: getBestListRowLabel(currentDialogValue, pendingRowIndex, listState, entry),
          path: pendingRowPath.slice(),
          pathLabel: pendingRowPath.length ? ("." + pendingRowPath.join(".")) : "",
          navigationTargetId: ""
        }
      ]);
    },
    stablePath: pendingRowPath.join(".") + ".dialog.new",
    rootEditorName: rootEditorMeta.rootEditorName ? rootEditorMeta.rootEditorName : (rootEditorMeta.editorName || ""),
    rootEditorLabel: rootEditorMeta.rootEditorLabel ? rootEditorMeta.rootEditorLabel : (rootEditorMeta.editorLabel || "")
  };

  var dialogDoc = {
    n: "ua.dialog",
    id: dialogId,
    title: editorContext.title,
    breadcrumbTitle: "Field Details",
    allowclose: true,
    fullscreen: true,
    breadcrumb: true,
    lockParent: true,
    inner: [
      {
        n: "p",
        c: "text-muted mb-2",
        i: editorContext.subtitle
      },
      {
        n: "div",
        id: detachedErrorId,
        c: "alert alert-danger d-none mt-3 mb-0",
        role: "alert"
      },
      {
        n: "div",
        id: editorMountId
      }
    ],
    footer: {
      n: "div",
      c: "col-12 d-flex justify-content-end gap-2",
      i: [
        {
          n: "button",
          type: "button",
          id: dialogId + "__cancel",
          c: "btn btn-outline-secondary",
          "data-ua-dismiss": "modal",
          i: "Cancel"
        },
        {
          n: "button",
          type: "button",
          id: dialogId + "__back",
          c: "btn btn-outline-secondary d-none",
          i: "Back"
        },
        {
          n: "button",
          type: "button",
          id: dialogId + "__save",
          c: "btn btn-primary",
          i: "Continue"
        }
      ]
    }
  };

  var dialogDom = await window.UATools.render(dialogDoc);
  document.body.appendChild(dialogDom);
  await mountDetachedSchemaEditor(dialogDom, editorMountId, detachedEditorOptions);
  runAfterRenderHooks();

  var detachedEditorRoot = findSchemaEditorRoot(dialogDom);
  attachDetachedNavigationContextResolver(detachedEditorRoot, function() {
    var detachedState = detachedEditorRoot && detachedEditorRoot.__uaSchemaEditor ? detachedEditorRoot.__uaSchemaEditor : null;
    var currentDialogValue = collectLiveEditorData(detachedEditorRoot, detachedState);
    return buildNavigationPathFromEntry(entry, rootEditorMeta).concat([
      {
        key: String(pendingRowIndex),
        label: getBestListRowLabel(currentDialogValue, pendingRowIndex, listState, entry),
        path: pendingRowPath.slice(),
        pathLabel: pendingRowPath.length ? ("." + pendingRowPath.join(".")) : "",
        navigationTargetId: ""
      }
    ]);
  });

  return await new Promise(function(resolve) {
    var saveButton = dialogDom.querySelector("#" + dialogId + "__save");
    var backButton = dialogDom.querySelector("#" + dialogId + "__back");
    var cancelButton = dialogDom.querySelector("#" + dialogId + "__cancel");

    var detachedErrorRecoveryBinding = bindDetachedDialogErrorRecovery(dialogDom, detachedErrorId);
    var detachedWizardBinding = bindDetachedWizardDialogActions(dialogDom, {
      backButton: backButton,
      primaryButton: saveButton,
      onFinalSubmit: async function() {
        clearDetachedDialogError(dialogDom, detachedErrorId);
        var saveResult = await saveDetachedSchemaEditor(detachedEditorRoot, "#" + dialogId, controlName);
        if (!saveResult || saveResult.success === false) {
          showDetachedDialogError(dialogDom, detachedErrorId, saveResult);
          return;
        }

        dialogTeardown.close(saveResult.data || {});
      }
    });

    var dialogTeardown = createDetachedDialogTeardown(root, entry, dialogDom, function(result) {
      resolve(result);
    }, function() {
      if (detachedErrorRecoveryBinding && typeof detachedErrorRecoveryBinding.disconnect === "function") {
        detachedErrorRecoveryBinding.disconnect();
      }
      if (detachedWizardBinding && typeof detachedWizardBinding.disconnect === "function") {
        detachedWizardBinding.disconnect();
      }
    });

    if (cancelButton) {
      cancelButton.addEventListener("click", function() {
        dialogTeardown.close(null);
      });
    }

    dialogTeardown.setReconcileResult(null);
    dialogTeardown.setWatchTimer(setInterval(function() {
      if (dialogTeardown.isFinalized()) return;
      dialogTeardown.reconcile(null);
    }, 150));
  });
}

async function addListRow(root, entry, listState, initialValue, options) {
  var host = root.querySelector("#" + listState.hostId);
  if (!host) return;
  options = options || {};

  var rowIndex = listState.nextRow++;
  var rowBase = listState.hostId + "__row_" + rowIndex;
  var rowTypeName = rowBase + "__type";
  var rowTypes = listState.itemTypes;
  var selectedType = inferTypeFromValue(initialValue, rowTypes);
  if (options.initialRowType && rowTypes.indexOf(options.initialRowType) >= 0) {
    selectedType = options.initialRowType;
  }
  var rowControlNames = {};
  var rowControlNamespaces = {};
  var rowPanels = [];
  var rowObjectEditors = {};

  var listItemTitle = listState.itemSpec.title || "Item";

  for (var i = 0; i < rowTypes.length; i++) {
    var rowType = rowTypes[i];
    var rowControlName = rowBase + "__" + rowType;
    rowControlNames[rowType] = rowControlName;

    var itemSpec = Object.assign({}, listState.itemSpec, { type: rowType });
    var controlMeta = getControlMetaForType(rowType, itemSpec);
    rowControlNamespaces[rowType] = controlMeta.namespace;
    var controlElement = stripUndefinedProperties(controlMeta.build({
      name: rowControlName,
      title: listItemTitle,
      required: false,
      value: rowType === selectedType ? initialValue : undefined,
      description: listState.itemSpec.description
    }));

    var panelClass = "ua-schema-editor-type-panel";
    if (rowTypes.length > 1 && rowType !== selectedType) panelClass += " d-none";

    if (rowType === "object") {
      rowObjectEditors[rowType] = {
        controlName: rowControlName,
        initialValue: (rowType === selectedType) ? initialValue : undefined
      };

      rowPanels.push({
        n: "div",
        c: panelClass,
        "data-fse-list-row-panel": rowBase,
        "data-fse-list-row-type": rowType,
        i: {
          n: "div",
          c: "d-flex align-items-center justify-content-between gap-2 flex-wrap",
          i: [
            {
              n: "div",
              "data-fse-list-summary": rowBase,
              "data-fse-list-summary-type": rowType,
              c: "text-muted small",
              i: summarizeObjectValue((rowType === selectedType) ? initialValue : undefined)
            },
            {
              n: "button",
              type: "button",
              c: "btn btn-outline-primary btn-sm",
              "data-fse-list-edit": rowBase,
              "data-fse-list-edit-type": rowType,
              i: [
                { n: "i", c: "bi bi-pencil-square me-1", i: "" },
                "Edit"
              ]
            }
          ]
        }
      });
      rowControlNames[rowType] = "";
    } else {
      rowPanels.push({
        n: "div",
        c: panelClass,
        "data-fse-list-row-panel": rowBase,
        "data-fse-list-row-type": rowType,
        i: controlElement
      });
    }
  }

  var rowPath = entry.path.concat([String(rowIndex)]);
  var rowNavigationTargetId = rowBase + "__nav";

  var rowDoc = {
    n: "div",
    id: rowNavigationTargetId,
    c: "ua-schema-editor-list-row",
    "data-fse-list-row": rowBase,
    "data-fse-row-path": rowPath.join("."),
    i: [
      {
        n: "div",
        c: "ua-schema-editor-list-toolbar",
        i: [
          {
            n: "button",
            type: "button",
            c: "btn btn-outline-secondary btn-sm ua-schema-editor-list-btn",
            "data-fse-list-up": rowBase,
            title: "Move up",
            i: { n: "i", c: "bi bi-chevron-up", i: "" }
          },
          {
            n: "button",
            type: "button",
            c: "btn btn-outline-secondary btn-sm ua-schema-editor-list-btn",
            "data-fse-list-down": rowBase,
            title: "Move down",
            i: { n: "i", c: "bi bi-chevron-down", i: "" }
          },
          {
            n: "button",
            type: "button",
            c: "btn btn-outline-danger btn-sm ua-schema-editor-list-btn",
            "data-fse-list-remove": rowBase,
            title: "Remove",
            i: { n: "i", c: "bi bi-trash3", i: "" }
          }
        ]
      }
    ]
  };

  if (rowTypes.length > 1) {
    rowDoc.i.push({
      n: "ua.select.dropdown",
      name: rowTypeName,
      title: "Item Type",
      required: true,
      data: rowTypes,
      value: selectedType
    });
  }

  for (var p = 0; p < rowPanels.length; p++) {
    rowDoc.i.push(rowPanels[p]);
  }

  var rowDom = await window.UATools.render(rowDoc);
  host.appendChild(rowDom);
  runAfterRenderHooks();

  var rowState = {
    rowBase: rowBase,
    rowPath: rowPath,
    navigationTargetId: rowNavigationTargetId,
    rowTypeName: rowTypes.length > 1 ? rowTypeName : "",
    rowTypes: rowTypes,
    controlNames: rowControlNames,
    controlNamespaces: rowControlNamespaces,
    values: {},
    pending: options.pending === true,
    entryTitle: listItemTitle,
    activeType: selectedType
  };
  if (initialValue !== undefined) {
    rowState.values[selectedType] = initialValue;
  }
  listState.rows.push(rowState);
  refreshListRowState(root, entry, listState);

  var removeButton = rowDom.querySelector("[data-fse-list-remove='" + rowBase + "']");
  if (removeButton) {
    removeButton.addEventListener("click", function () {
      var rowEl = root.querySelector("[data-fse-list-row='" + rowBase + "']");
      if (rowEl && rowEl.parentNode) rowEl.parentNode.removeChild(rowEl);
      listState.rows = listState.rows.filter(function (r) { return r.rowBase !== rowBase; });
      refreshListRowState(root, entry, listState);
      notifyEditorMutation(root, {
        kind: "list-remove",
        path: entry.path,
        rowBase: rowBase
      });
    });
  }

  var upButton = rowDom.querySelector("[data-fse-list-up='" + rowBase + "']");
  if (upButton) {
    upButton.addEventListener("click", function () {
      var index = -1;
      for (var i = 0; i < listState.rows.length; i++) {
        if (listState.rows[i].rowBase === rowBase) {
          index = i;
          break;
        }
      }
      if (index <= 0) return;

      var rowEl = root.querySelector("[data-fse-list-row='" + rowBase + "']");
      if (!rowEl || !rowEl.parentNode) return;

      var prevRow = listState.rows[index - 1];
      var prevEl = root.querySelector("[data-fse-list-row='" + prevRow.rowBase + "']");
      if (!prevEl || !prevEl.parentNode) return;

      rowEl.parentNode.insertBefore(rowEl, prevEl);
      var tmp = listState.rows[index - 1];
      listState.rows[index - 1] = listState.rows[index];
      listState.rows[index] = tmp;
      refreshListRowState(root, entry, listState);
      notifyEditorMutation(root, {
        kind: "list-reorder",
        path: entry.path,
        rowBase: rowBase,
        direction: "up"
      });
    });
  }

  var downButton = rowDom.querySelector("[data-fse-list-down='" + rowBase + "']");
  if (downButton) {
    downButton.addEventListener("click", function () {
      var index = -1;
      for (var i = 0; i < listState.rows.length; i++) {
        if (listState.rows[i].rowBase === rowBase) {
          index = i;
          break;
        }
      }
      if (index < 0 || index >= listState.rows.length - 1) return;

      var rowEl = root.querySelector("[data-fse-list-row='" + rowBase + "']");
      if (!rowEl || !rowEl.parentNode) return;

      var nextRow = listState.rows[index + 1];
      var nextEl = root.querySelector("[data-fse-list-row='" + nextRow.rowBase + "']");
      if (!nextEl || !nextEl.parentNode) return;

      rowEl.parentNode.insertBefore(nextEl, rowEl);
      var tmp = listState.rows[index + 1];
      listState.rows[index + 1] = listState.rows[index];
      listState.rows[index] = tmp;
      refreshListRowState(root, entry, listState);
      notifyEditorMutation(root, {
        kind: "list-reorder",
        path: entry.path,
        rowBase: rowBase,
        direction: "down"
      });
    });
  }

  if (rowTypes.length > 1) {
    var rowTypeSelector = rowDom.querySelector("[name='" + rowTypeName + "']");
    if (rowTypeSelector) {
      rowTypeSelector.addEventListener("change", async function () {
        var previousType = rowState.activeType || rowTypes[0];
        if (previousType && previousType !== rowTypeSelector.value) {
          await cacheListRowTypeValue(root, rowState, previousType);
        }
        await applyListRowTypeSelection(root, rowDom, rowBase, rowState, rowTypeSelector.value);
        rowState.activeType = rowTypeSelector.value;
        notifyEditorMutation(root, {
          kind: "list-row-type-change",
          path: entry.path,
          rowBase: rowBase,
          rowType: rowTypeSelector.value
        });
      });
    }
  }

  for (var rowTypeIndex = 0; rowTypeIndex < rowTypes.length; rowTypeIndex++) {
    (function(rowType) {
      if (rowType === "object") return;
      var controlName = rowControlNames[rowType];
      if (!controlName) return;
      var control = rowDom.querySelector("[name='" + controlName + "']");
      if (!control) return;

      function syncRowValue() {
        var currentValue = readControlValueFromDom(rowDom, controlName);
        if (currentValue === undefined) return;
        if (!rowState.values || typeof rowState.values !== "object") {
          rowState.values = {};
        }
        rowState.values[rowType] = currentValue;
      }

      control.addEventListener("input", syncRowValue);
      control.addEventListener("change", syncRowValue);
    })(rowTypes[rowTypeIndex]);
  }

  var editButtons = rowDom.querySelectorAll("[data-fse-list-edit='" + rowBase + "']");
  for (var b = 0; b < editButtons.length; b++) {
    (function (button) {
      button.addEventListener("click", async function () {
        var editType = button.getAttribute("data-fse-list-edit-type");
        var editor = rowObjectEditors[editType];
        if (!editor) return;
        var currentRowValue = (rowState && rowState.values && typeof rowState.values[editType] === "object")
          ? rowState.values[editType]
          : editor.initialValue;
        await openObjectListEditor(root, entry, rowBase, editType, editor.controlName, rowState, listState, currentRowValue);
      });
    })(editButtons[b]);
  }

}

async function bindListEditors(root, metadata, sourceData, options) {
  if (!root || !metadata || !metadata.entries) return;
  options = options || {};
  var preserveExisting = options.preserveExisting === true;

  for (var i = 0; i < metadata.entries.length; i++) {
    var entry = metadata.entries[i];
    if (!entry.descriptor || !entry.descriptor.listStates) continue;

    var selectedType = getSelectedTypeForEntry(entry, root);
    if (selectedType !== "list") continue;

    var listState = entry.descriptor.listStates[selectedType];
    if (!listState) continue;

    var host = root.querySelector("#" + listState.hostId);
    if (!host) continue;

    if (preserveExisting && listState.hasLiveState === true) {
      continue;
    }

    host.innerHTML = "";
    listState.rows = [];
    listState.nextRow = 0;

    var listValue = getListValueAtPath(sourceData, entry.path, entry);
    for (var li = 0; li < listValue.length; li++) {
      await addListRow(root, entry, listState, listValue[li]);
    }
    listState.hasLiveState = true;

    var addButton = root.querySelector("#" + listState.addButtonId);
    if (addButton && !addButton.__fseBound) {
      addButton.__fseBound = true;
      (function (entryRef, listStateRef, buttonRef) {
        buttonRef.addEventListener("click", async function () {
          if (listStateRef.itemTypes.length === 1 && listStateRef.itemTypes.indexOf("object") >= 0) {
            var newObjectValue = await collectObjectListValue(root, entryRef, listStateRef, {});
            if (newObjectValue === null) return;
            await addListRow(root, entryRef, listStateRef, newObjectValue, {});
            notifyEditorMutation(root, {
              kind: "list-add",
              path: entryRef.path,
              itemType: "object"
            });
            return;
          }

          var preferredItemType = "";
          for (var itemTypeIndex = 0; itemTypeIndex < listStateRef.itemTypes.length; itemTypeIndex++) {
            var candidateType = listStateRef.itemTypes[itemTypeIndex];
            if (candidateType === "object") continue;
            preferredItemType = candidateType;
            break;
          }
          if (!preferredItemType) {
            preferredItemType = listStateRef.itemTypes[0] || "string";
          }

          await addListRow(root, entryRef, listStateRef, "", {
            pending: true,
            initialRowType: preferredItemType
          });
          notifyEditorMutation(root, {
            kind: "list-add",
            path: entryRef.path,
            itemType: preferredItemType
          });
        });
      })(entry, listState, addButton);
    }
  }
}

function bindTypeSelectors(root, metadata) {
  if (!root || !metadata || !metadata.entries) {
    return;
  }

  function applyVisibility(entryIndex, selectedType) {
    var panels = root.querySelectorAll("[data-fse-panel='" + entryIndex + "']");

    for (var j = 0; j < panels.length; j++) {
      var panel = panels[j];
      var panelType = panel.getAttribute("data-fse-type");

      if (panelType === selectedType) {
        panel.classList.remove("d-none");
      } else {
        panel.classList.add("d-none");
      }
    }
  }

  for (var i = 0; i < metadata.entries.length; i++) {
    var entry = metadata.entries[i];
    if (entry.types.length <= 1 || !entry.descriptor.typeSelectorName) {
      continue;
    }

    var selector = root.querySelector("[name='" + entry.descriptor.typeSelectorName + "']");
    if (!selector) {
      continue;
    }

    (function (entryRef, selectorRef) {
      var initialSelectedType = selectorRef.value || entryRef.descriptor.initialType || entryRef.types[0];
      selectorRef.setAttribute('data-fse-previous-type', initialSelectedType);
      applyVisibility(entryRef.index, initialSelectedType);
      syncMixedInlineObjectBranchVisibility(root, metadata);
      selectorRef.addEventListener("change", async function () {
        var previousType = selectorRef.getAttribute('data-fse-previous-type') || entryRef.descriptor.initialType || entryRef.types[0];
        var nextType = selectorRef.value || entryRef.types[0];

        if (
          previousType !== nextType &&
          entryRef.descriptor.controlNamespaces &&
          entryRef.descriptor.controlNamespaces[previousType] === 'ua.fields.schema.editor'
        ) {
          if (!entryRef.values || typeof entryRef.values !== 'object') {
            entryRef.values = {};
          }
          entryRef.values[previousType] = await getCurrentEntryBranchValue(root, metadata, entryRef, previousType);
        }

        applyVisibility(entryRef.index, nextType);
        syncMixedInlineObjectBranchVisibility(root, metadata);
        bindFieldTypeVisibility(root, metadata);
        await bindListEditors(root, metadata, metadata.sourceData || {}, { preserveExisting: true });

        if (
          entryRef.values &&
          Object.prototype.hasOwnProperty.call(entryRef.values, nextType) &&
          entryRef.descriptor.controlNamespaces &&
          entryRef.descriptor.controlNamespaces[nextType] === 'ua.fields.schema.editor'
        ) {
          await applyEntryBranchValue(root, metadata, entryRef, nextType, entryRef.values[nextType]);
        }

        selectorRef.setAttribute('data-fse-previous-type', nextType);
      });
    })(entry, selector);
  }
}

function getFieldTypeEntry(metadata) {
  if (!metadata || !(metadata.entries instanceof Array)) return null;
  if (metadata.fieldTypeEntryIndex === undefined || metadata.fieldTypeEntryIndex === null) return null;

  for (var i = 0; i < metadata.entries.length; i++) {
    if (metadata.entries[i].index === metadata.fieldTypeEntryIndex) {
      return metadata.entries[i];
    }
  }
  return null;
}

function getSelectedFieldTypes(root, metadata) {
  var entry = getFieldTypeEntry(metadata);
  if (!entry || !entry.descriptor) return [];

  var selected = [];
  var primaryType = entry.types[0];
  var controlName = entry.descriptor.controlNames[primaryType];
  if (!controlName) return [];

  var checks = root.querySelectorAll("[name='" + controlName + "'] input[data-field-type-option='true']:checked");
  for (var i = 0; i < checks.length; i++) {
    var value = normalizeSingleType(checks[i].value || "");
    if (!value) continue;
    if (selected.indexOf(value) >= 0) continue;
    selected.push(value);
  }

  if (selected.length > 0) return selected;

  var fallback = normalizeTypeSelection(entry.descriptor.initialValue);
  if (fallback.length > 0) return fallback;

  if (entry.spec && Object.prototype.hasOwnProperty.call(entry.spec, "default")) {
    return normalizeTypeSelection(entry.spec.default);
  }

  return [];
}

function bindFieldTypeVisibility(root, metadata) {
  if (!root || !metadata || !metadata.isFieldSchemaMode) return;
  var selectedTypes = getSelectedFieldTypes(root, metadata);

  if (metadata.entries instanceof Array) {
    for (var entryIndex = 0; entryIndex < metadata.entries.length; entryIndex++) {
      applyEntryPresentation(root, metadata.entries[entryIndex], selectedTypes);
    }
  }

  var targets = root.querySelectorAll("[data-fse-entry-types]");
  for (var i = 0; i < targets.length; i++) {
    var node = targets[i];
    var allowed = (node.getAttribute("data-fse-entry-types") || "").toString().split(",").map(function(item) {
      return normalizeSingleType(item || "");
    }).filter(function(item) {
      return !!item;
    });

    var show = false;
    if (allowed.length === 0) {
      show = true;
    } else {
      for (var a = 0; a < allowed.length; a++) {
        if (selectedTypes.indexOf(allowed[a]) >= 0) {
          show = true;
          break;
        }
      }
    }

    if (show) node.classList.remove("d-none");
    else node.classList.add("d-none");
  }

  var steps = root.querySelectorAll("[data-wizard-title]");
  for (var s = 0; s < steps.length; s++) {
    var step = steps[s];
    var entrySections = step.querySelectorAll("[data-fse-entry-index]");
    var stepVisible = entrySections.length === 0;
    for (var e = 0; e < entrySections.length; e++) {
      if (!entrySections[e].classList.contains("d-none")) {
        stepVisible = true;
        break;
      }
    }

    step.setAttribute("data-fse-step-hidden", stepVisible ? "false" : "true");
    var wizardStep = step.closest ? step.closest("[data-wizard-step]") : null;
    if (wizardStep && typeof wizardStep.setAttribute === "function") {
      wizardStep.setAttribute("data-wizard-step-hidden", stepVisible ? "false" : "true");
    }
  }

  try {
    var wizardRoot = root.querySelector("[data-wizard='true']");
    if (wizardRoot) {
      var wizardSteps = wizardRoot.querySelectorAll("[data-wizard-step]");
      var visibleStepNumbers = [];
      for (var ws = 0; ws < wizardSteps.length; ws++) {
        var wizardStep = wizardSteps[ws];
        var hidden = wizardStep.getAttribute("data-wizard-step-hidden") === "true";
        var stepNum = Number(wizardStep.getAttribute("data-wizard-step") || "0");
        if (hidden) {
          wizardStep.classList.add("d-none");
        } else if (stepNum > 0) {
          visibleStepNumbers.push(stepNum);
        }
      }

      var navButtons = wizardRoot.querySelectorAll("[data-wizard-nav-step]");
      for (var nb = 0; nb < navButtons.length; nb++) {
        var button = navButtons[nb];
        var targetStep = Number(button.getAttribute("data-wizard-nav-step") || "0");
        var visibleIndex = visibleStepNumbers.indexOf(targetStep);
        var pill = button.querySelector(".ua-wizard-step-pill");
        if (visibleIndex >= 0) {
          button.classList.remove("d-none");
          if (pill) pill.textContent = String(visibleIndex + 1);
        } else {
          button.classList.add("d-none");
        }
      }
    }

    if (wizardRoot && typeof wizardRoot.dispatchEvent === "function") {
      var visibilityEvent = createDomEvent(wizardRoot, "ua:wizard:visibility-sync", {
        bubbles: false,
        detail: {
          source: "ua.fields.schema.editor",
          selectedTypes: selectedTypes.slice()
        }
      });
      if (visibilityEvent) wizardRoot.dispatchEvent(visibilityEvent);
    }
  } catch (error) {}
}

function bindFieldTypeSelectorControl(root, metadata) {
  if (!root || !metadata || !metadata.isFieldSchemaMode) return;
  var entry = getFieldTypeEntry(metadata);
  if (!entry || !entry.descriptor) return;

  var controlName = entry.descriptor.controlNames[entry.types[0]];
  if (!controlName) return;

  var host = root.querySelector("[name='" + controlName + "']");
  if (!host || host.__fseFieldTypeBound) return;
  host.__fseFieldTypeBound = true;

  host.addEventListener("change", async function() {
    bindFieldTypeVisibility(root, metadata);
    await bindListEditors(root, metadata, metadata.sourceData || {}, { preserveExisting: true });
  });
}

function bindObjectDialogEditors(root, metadata) {
  if (!root || !metadata || !metadata.entries) return;

  for (var i = 0; i < metadata.entries.length; i++) {
    var entry = metadata.entries[i];
    if (!(entry && entry.descriptor && entry.descriptor.objectDialogs)) continue;

    var objectTypes = Object.keys(entry.descriptor.objectDialogs);
    for (var t = 0; t < objectTypes.length; t++) {
      var objectType = objectTypes[t];
      var button = root.querySelector("[data-fse-object-edit='" + entry.descriptor.baseName + "'][data-fse-object-edit-type='" + objectType + "']");
      if (!button || button.__fseObjectDialogBound) continue;
      button.__fseObjectDialogBound = true;
      (function(entryRef, objectTypeRef, buttonRef) {
        buttonRef.addEventListener("click", async function() {
          await openObjectEntryEditor(root, entryRef, objectTypeRef);
        });
      })(entry, objectType, button);
    }
  }
}

function humanizeNavigationSegment(value) {
  var text = safeDisplayText(value, "").trim();
  if (!text) return "Field";
  return text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, function(chr) { return chr.toUpperCase(); });
}

function getFieldTypeSpecificPresentation(entryKey, selectedTypes, fallbackTitle, fallbackDescription) {
  var key = safeDisplayText(entryKey, "").trim().toLowerCase();
  var normalizedTypes = (selectedTypes instanceof Array ? selectedTypes : []).map(function(type) {
    return normalizeSingleType(type);
  }).filter(Boolean);

  if (normalizedTypes.length === 1 && normalizedTypes[0] === "string") {
    if (key === "min") {
      return {
        title: "Minimum Length",
        description: "The minimum number of characters allowed."
      };
    }
    if (key === "max") {
      return {
        title: "Maximum Length",
        description: "The maximum number of characters allowed."
      };
    }
  }

  return {
    title: safeDisplayText(fallbackTitle, humanizeNavigationSegment(entryKey || "Field")),
    description: safeDisplayText(fallbackDescription, "")
  };
}

function applyEntryPresentation(root, entry, selectedTypes) {
  if (!(root && entry && entry.path instanceof Array)) return;
  var section = root.querySelector("[data-fse-entry-index='" + entry.index + "']");
  if (!section) return;

  var presentation = getFieldTypeSpecificPresentation(
    entry.key,
    selectedTypes,
    entry.title,
    entry.description
  );

  var labels = section.querySelectorAll("label");
  for (var i = 0; i < labels.length; i++) {
    if (labels[i] && labels[i].textContent !== undefined) {
      labels[i].textContent = presentation.title;
    }
  }

  var titledControls = section.querySelectorAll("input[title], textarea[title], select[title]");
  for (var j = 0; j < titledControls.length; j++) {
    titledControls[j].setAttribute("title", presentation.title);
  }

  var descriptions = section.querySelectorAll(".form-text, [id$='-description']");
  for (var d = 0; d < descriptions.length; d++) {
    if (!descriptions[d] || descriptions[d].textContent === undefined) continue;
    descriptions[d].textContent = presentation.description;
  }
}

function cloneNavigationSegments(segments) {
  if (!(segments instanceof Array)) return [];
  return segments.map(function(segment) {
    var next = segment && typeof segment === "object" ? segment : {};
    return {
      key: safeDisplayText(next.key, ""),
      label: safeDisplayText(next.label, humanizeNavigationSegment(next.key || "Field")),
      path: next.path instanceof Array ? next.path.slice() : [],
      pathLabel: safeDisplayText(next.pathLabel, next.path instanceof Array && next.path.length ? ("." + next.path.join(".")) : ""),
      navigationTargetId: safeDisplayText(next.navigationTargetId, ""),
      canNavigate: next.canNavigate !== false,
      stepNumber: Number(next.stepNumber || 0) || 0
    };
  });
}

function readLiveEntryValue(root, entry) {
  if (!(root && entry && entry.descriptor)) return undefined;

  var selectedType = getSelectedTypeForEntry(entry, root);
  if (selectedType === "list") {
    var listState = entry.descriptor.listStates && entry.descriptor.listStates[selectedType];
    if (!(listState && listState.rows instanceof Array)) return [];

    var items = [];
    for (var i = 0; i < listState.rows.length; i++) {
      items.push(getCurrentListRowValue(root, listState.rows[i]));
    }
    return items;
  }

  if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
    return entry.descriptor.objectDialogs[selectedType].value;
  }

  var controlName = entry.descriptor.controlNames[selectedType] || entry.descriptor.controlNames[entry.types[0]];
  if (!controlName) return undefined;
  return readControlValueFromDom(root, controlName);
}

function collectLiveEditorData(root, metadata) {
  var output = {};
  if (!(root && metadata && metadata.entries instanceof Array)) return output;

  for (var i = 0; i < metadata.entries.length; i++) {
    var entry = metadata.entries[i];
    if (!entry || entry.nestedOnly) continue;
    var value = readLiveEntryValue(root, entry);
    if (value === undefined) continue;
    setByPath(output, entry.path, value);
  }

  return output;
}

function findSchemaEditorRoot(node) {
  if (!node || !node.querySelectorAll) return null;
  if (node.__uaSchemaEditor) return node;

  var candidates = node.querySelectorAll("*");
  for (var i = 0; i < candidates.length; i++) {
    if (candidates[i].__uaSchemaEditor) return candidates[i];
  }

  return null;
}

function attachDetachedNavigationContextResolver(schemaEditorRoot, resolver) {
  if (!(schemaEditorRoot && schemaEditorRoot.__uaSchemaEditor) || typeof resolver !== "function") return;
  schemaEditorRoot.__uaSchemaEditor.navigationContextSegmentsResolver = resolver;
}

async function flushDetachedEditorActiveElement(scopeRoot) {
  if (!(typeof document !== "undefined" && document)) return;
  var activeElement = document.activeElement;
  if (!(activeElement && scopeRoot && typeof scopeRoot.contains === "function" && scopeRoot.contains(activeElement))) return;

  if (typeof activeElement.dispatchEvent === "function") {
    var inputEvent = createDomEvent(activeElement, "input", { bubbles: true });
    if (inputEvent) activeElement.dispatchEvent(inputEvent);
    var changeEvent = createDomEvent(activeElement, "change", { bubbles: true });
    if (changeEvent) activeElement.dispatchEvent(changeEvent);
  }

  if (typeof activeElement.blur === "function") {
    activeElement.blur();
  }

  await new Promise(function(resolve) {
    setTimeout(resolve, 0);
  });
}

async function saveDetachedSchemaEditor(schemaEditorRoot, selector, controlName) {
  var resolvedSchemaEditorRoot = schemaEditorRoot;
  var detachedState = resolvedSchemaEditorRoot && resolvedSchemaEditorRoot.__uaSchemaEditor ? resolvedSchemaEditorRoot.__uaSchemaEditor : null;
  var selectorRoot = null;

  if (selector && typeof document !== "undefined" && document && typeof document.querySelector === "function") {
    selectorRoot = document.querySelector(selector);
  }

  if (!detachedState && selectorRoot) {
    var liveSchemaEditorRoot = findSchemaEditorRoot(selectorRoot);
    if (liveSchemaEditorRoot && liveSchemaEditorRoot.__uaSchemaEditor) {
      resolvedSchemaEditorRoot = liveSchemaEditorRoot;
      detachedState = liveSchemaEditorRoot.__uaSchemaEditor;
    }
  }

  await flushDetachedEditorActiveElement(resolvedSchemaEditorRoot || selectorRoot || null);

  if (detachedState && resolvedSchemaEditorRoot) {
    return await exports.save(detachedState.editorName || detachedState.wizardName || "", resolvedSchemaEditorRoot, {});
  }

  if (selector) {
    var detachedSaveResult = await window.UATools.save(selector, true);
    if (detachedSaveResult && detachedSaveResult.success !== false) {
      var detachedControlName = controlName || (detachedState ? (detachedState.editorName || detachedState.wizardName || "") : "");
      var directControlSave = detachedSaveResult[detachedControlName];
      if (directControlSave && directControlSave.success !== false) {
        return {
          success: true,
          namespace: exports.define.namespace,
          data: directControlSave.data || {},
          errors: []
        };
      }
    }
  }

  return {
    success: false,
    namespace: exports.define.namespace,
    data: {},
    errors: [createValidationError("Detached schema editor state is unavailable.")]
  };
}

function getDetachedDialogErrorNode(dialogDom, errorNodeOrId) {
  if (!(dialogDom && errorNodeOrId)) return null;
  if (typeof errorNodeOrId !== "string") return errorNodeOrId;
  if (typeof dialogDom.querySelector !== "function") return null;
  return dialogDom.querySelector("#" + errorNodeOrId);
}

function collectDetachedDialogErrorMessages(saveResult) {
  var errors = saveResult && saveResult.errors instanceof Array ? saveResult.errors : [];
  var messages = [];

  for (var i = 0; i < errors.length; i++) {
    var error = errors[i] || {};
    var message = safeDisplayText(error.message, "").trim();
    if (!message) continue;
    if (messages.indexOf(message) >= 0) continue;
    messages.push(message);
  }

  if (messages.length === 0) {
    messages.push("Unable to save this field right now. Please try again.");
  }

  return messages;
}

function formatDetachedDialogErrors(saveResult) {
  return collectDetachedDialogErrorMessages(saveResult).join("\n");
}

function clearDetachedDialogError(dialogDom, errorNodeOrId) {
  var errorNode = getDetachedDialogErrorNode(dialogDom, errorNodeOrId);
  if (!errorNode) return;
  errorNode.textContent = "";
  errorNode.removeAttribute("tabindex");
  errorNode.classList.add("d-none");
}

function showDetachedDialogError(dialogDom, errorNodeOrId, saveResult) {
  var errorNode = getDetachedDialogErrorNode(dialogDom, errorNodeOrId);
  if (!errorNode) return;

  var messages = collectDetachedDialogErrorMessages(saveResult);
  errorNode.textContent = "";

  if (messages.length === 1) {
    errorNode.textContent = messages[0];
  } else {
    var list = document.createElement("ul");
    list.className = "mb-0 ps-3";
    for (var i = 0; i < messages.length; i++) {
      var item = document.createElement("li");
      item.textContent = messages[i];
      list.appendChild(item);
    }
    errorNode.appendChild(list);
  }

  errorNode.setAttribute("tabindex", "-1");
  errorNode.classList.remove("d-none");

  if (typeof errorNode.scrollIntoView === "function") {
    try {
      errorNode.scrollIntoView({ block: "nearest", inline: "nearest" });
    } catch (scrollError) {
      errorNode.scrollIntoView();
    }
  }

  if (typeof errorNode.focus === "function") {
    try {
      errorNode.focus({ preventScroll: true });
    } catch (focusError) {
      errorNode.focus();
    }
  }
}

function bindDetachedDialogErrorRecovery(dialogDom, errorNodeOrId, options) {
  if (!(dialogDom && errorNodeOrId)) return null;
  options = options || {};
  var disconnected = false;
  var lastWizardStep = null;

  function clearIfVisible() {
    if (disconnected) return;
    var errorNode = getDetachedDialogErrorNode(dialogDom, errorNodeOrId);
    if (!(errorNode && errorNode.classList && !errorNode.classList.contains("d-none"))) return;
    clearDetachedDialogError(dialogDom, errorNode);
  }

  function shouldClearForEventTarget(target) {
    if (!(target && target.nodeType === 1)) return false;
    var errorNode = getDetachedDialogErrorNode(dialogDom, errorNodeOrId);
    if (errorNode && (target === errorNode || (errorNode.contains && errorNode.contains(target)))) return false;
    return !!(target.matches && target.matches("input, select, textarea, button, [contenteditable='true'], [contenteditable=''], [data-wizard-nav-step]"));
  }

  var onInput = function(event) {
    if (shouldClearForEventTarget(event.target || null)) clearIfVisible();
  };

  var onChange = function(event) {
    if (shouldClearForEventTarget(event.target || null)) clearIfVisible();
  };

  var onWizardVisibilitySync = function() {
    var wizardRoot = dialogDom.querySelector("[data-wizard='true']");
    var currentStep = wizardRoot ? String(wizardRoot.getAttribute("data-wizard-current") || "") : "";
    if (lastWizardStep === null) {
      lastWizardStep = currentStep;
      return;
    }
    if (currentStep && currentStep !== lastWizardStep) {
      lastWizardStep = currentStep;
      clearIfVisible();
      return;
    }
    lastWizardStep = currentStep;
  };

  dialogDom.addEventListener("input", onInput, true);
  dialogDom.addEventListener("change", onChange, true);
  dialogDom.addEventListener("ua:wizard:visibility-sync", onWizardVisibilitySync);
  onWizardVisibilitySync();

  return {
    clear: clearIfVisible,
    disconnect: function() {
      disconnected = true;
      dialogDom.removeEventListener("input", onInput, true);
      dialogDom.removeEventListener("change", onChange, true);
      dialogDom.removeEventListener("ua:wizard:visibility-sync", onWizardVisibilitySync);
    }
  };
}

function bindDetachedWizardDialogActions(dialogDom, options) {
  if (!(dialogDom && options)) return null;

  var externalBackButton = options.backButton || null;
  var externalPrimaryButton = options.primaryButton || null;
  var mutationObserver = null;
  var syncScheduled = false;
  var syncingExternalButtons = false;
  var disconnected = false;
  var detachedFooterMarkerAttribute = "data-detached-footer-hidden";
  var detachedFooterIgnoredAttributes = {
    "class": true,
    "hidden": true,
    "style": true,
    "aria-hidden": true
  };
  detachedFooterIgnoredAttributes[detachedFooterMarkerAttribute] = true;

  function shouldIgnoreSelfAuthoredFooterMutation(mutation) {
    if (!mutation) return false;
    var target = mutation.target || null;
    if (!(target && target.nodeType === 1 && target.classList && target.classList.contains("wizard-footer"))) {
      return false;
    }
    if (target.getAttribute(detachedFooterMarkerAttribute) !== "true") return false;
    if (mutation.type !== "attributes") return false;
    return !!detachedFooterIgnoredAttributes[mutation.attributeName || ""];
  }

  function mutationTargetsWizard(parts, mutation) {
    if (!mutation) return false;
    var target = mutation.target || null;
    var wizardRoot = parts && parts.wizardRoot ? parts.wizardRoot : null;
    if (wizardRoot) {
      return !!(target && wizardRoot.contains(target));
    }
    return mutation.type === "childList";
  }

  function isElementHiddenByState(node) {
    if (!(node && typeof node.getAttribute === "function")) return true;

    var detachedHiddenFooter = node.closest ? node.closest('.wizard-footer[' + detachedFooterMarkerAttribute + '="true"]') : null;
    var current = node;
    while (current && current.nodeType === 1) {
      var ignoreCurrentState = current !== node && current.classList && current.classList.contains("wizard-footer");
      if (!ignoreCurrentState && typeof current.hasAttribute === "function") {
        if (current.hasAttribute("hidden")) return true;
      }

      if (!ignoreCurrentState && typeof current.getAttribute === "function") {
        var ariaHidden = (current.getAttribute("aria-hidden") || "").toString().trim().toLowerCase();
        if (ariaHidden === "true") return true;
      }

      if (!ignoreCurrentState && current.classList && current.classList.contains("d-none")) return true;

      if (!ignoreCurrentState && current.style) {
        var inlineDisplay = (current.style.display || "").toString().trim().toLowerCase();
        if (inlineDisplay === "none") return true;
        var inlineVisibility = (current.style.visibility || "").toString().trim().toLowerCase();
        if (inlineVisibility === "hidden" || inlineVisibility === "collapse") return true;
      }

      var ownerDocument = current.ownerDocument || (typeof document !== "undefined" ? document : null);
      var defaultView = ownerDocument && ownerDocument.defaultView ? ownerDocument.defaultView : null;
      if (!ignoreCurrentState && !(detachedHiddenFooter && current === node) && defaultView && typeof defaultView.getComputedStyle === "function") {
        var computedStyle = defaultView.getComputedStyle(current);
        if (computedStyle) {
          var computedDisplay = (computedStyle.display || "").toString().trim().toLowerCase();
          if (computedDisplay === "none") return true;
          var computedVisibility = (computedStyle.visibility || "").toString().trim().toLowerCase();
          if (computedVisibility === "hidden" || computedVisibility === "collapse") return true;
        }
      }

      current = current.parentNode;
    }

    return false;
  }

  function isElementActionable(node) {
    if (!(node && typeof node.click === "function")) return false;
    if (node.disabled) return false;
    if (isElementHiddenByState(node)) return false;
    return true;
  }

  function resolveWizardParts() {
    var wizardRoot = dialogDom.querySelector("[data-wizard='true']");
    var wizardId = wizardRoot ? (wizardRoot.getAttribute("id") || "") : "";
    var internalBackButton = wizardId ? dialogDom.querySelector("#" + wizardId + "-back") : null;
    var internalContinueButton = wizardId ? dialogDom.querySelector("#" + wizardId + "-continue") : null;
    return {
      wizardRoot: wizardRoot,
      wizardId: wizardId,
      internalBackButton: internalBackButton,
      internalContinueButton: internalContinueButton,
      hasBackAction: !!internalBackButton,
      hasContinueAction: !!internalContinueButton,
      footers: dialogDom.querySelectorAll(".wizard-footer")
    };
  }

  function hideInternalFooters(parts) {
    var footers = parts && parts.footers ? parts.footers : [];
    for (var fi = 0; fi < footers.length; fi++) {
      var footer = footers[fi];
      if (!(footer && footer.classList)) continue;

      var alreadyHidden = footer.getAttribute(detachedFooterMarkerAttribute) === "true";
      var changed = false;

      if (!footer.classList.contains("d-none")) {
        footer.classList.add("d-none");
        changed = true;
      }
      if (footer.getAttribute("aria-hidden") !== "true") {
        footer.setAttribute("aria-hidden", "true");
        changed = true;
      }
      if (!footer.hasAttribute("hidden")) {
        footer.setAttribute("hidden", "hidden");
        changed = true;
      }
      if ((footer.style.display || "").toString().trim().toLowerCase() !== "none") {
        footer.style.display = "none";
        changed = true;
      }
      if ((footer.style.pointerEvents || "").toString().trim().toLowerCase() !== "none") {
        footer.style.pointerEvents = "none";
        changed = true;
      }
      if ((footer.style.visibility || "").toString().trim().toLowerCase() !== "hidden") {
        footer.style.visibility = "hidden";
        changed = true;
      }
      if ((footer.style.margin || "").toString().trim() !== "0px" && (footer.style.margin || "").toString().trim() !== "0") {
        footer.style.margin = "0";
        changed = true;
      }
      if ((footer.style.padding || "").toString().trim() !== "0px" && (footer.style.padding || "").toString().trim() !== "0") {
        footer.style.padding = "0";
        changed = true;
      }
      if ((footer.style.minHeight || "").toString().trim() !== "0px" && (footer.style.minHeight || "").toString().trim() !== "0") {
        footer.style.minHeight = "0";
        changed = true;
      }
      if ((footer.style.height || "").toString().trim() !== "0px" && (footer.style.height || "").toString().trim() !== "0") {
        footer.style.height = "0";
        changed = true;
      }
      if ((footer.style.zIndex || "").toString().trim() !== "auto") {
        footer.style.zIndex = "auto";
        changed = true;
      }
      if (!alreadyHidden || changed) {
        footer.setAttribute(detachedFooterMarkerAttribute, "true");
      }
    }
  }

  function getVisibleSteps(parts) {
    var resolvedParts = parts || resolveWizardParts();
    var wizardRoot = resolvedParts.wizardRoot;
    if (!wizardRoot) return [];
    hideInternalFooters(resolvedParts);
    var stepNodes = wizardRoot.querySelectorAll("[data-wizard-step]");
    var visible = [];
    for (var i = 0; i < stepNodes.length; i++) {
      if (stepNodes[i].getAttribute("data-wizard-step-hidden") === "true") continue;
      if (stepNodes[i].getAttribute("data-fse-step-hidden") === "true") continue;
      visible.push(Number(stepNodes[i].getAttribute("data-wizard-step") || "0"));
    }
    if (visible.length === 0) {
      visible.push(Number(wizardRoot.getAttribute("data-wizard-current") || "1"));
    }
    return visible;
  }

  function isFinalVisibleStep() {
    var parts = resolveWizardParts();
    if (!(parts.wizardRoot && parts.hasContinueAction)) return false;
    var visibleSteps = getVisibleSteps(parts);
    if (visibleSteps.length === 0) return false;
    var currentStep = Number(parts.wizardRoot.getAttribute("data-wizard-current") || "1");
    return currentStep === visibleSteps[visibleSteps.length - 1];
  }

  async function handleFinalSubmit() {
    if (typeof options.onFinalSubmit !== "function") return;
    await options.onFinalSubmit();
  }

  function escapeButtonText(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function syncExternalButtons() {
    if (syncingExternalButtons) return;
    syncingExternalButtons = true;

    try {
      var parts = resolveWizardParts();
      var wizardRoot = parts.wizardRoot;
      var internalBackButton = parts.internalBackButton;
      var internalContinueButton = parts.internalContinueButton;
      hideInternalFooters(parts);

      if (!(wizardRoot && parts.hasContinueAction)) {
        if (externalBackButton) {
          externalBackButton.classList.add("d-none");
          externalBackButton.disabled = true;
        }
        if (externalPrimaryButton) {
          externalPrimaryButton.disabled = true;
          if (externalPrimaryButton.getAttribute("data-dialog-final-step") !== "false") {
            externalPrimaryButton.setAttribute("data-dialog-final-step", "false");
          }
        }
        return;
      }

      var finalVisibleStep = isFinalVisibleStep();
      if (externalBackButton) {
        var showBack = !!(parts.hasBackAction && isElementActionable(internalBackButton));
        externalBackButton.classList.toggle("d-none", !showBack);
        externalBackButton.disabled = !showBack;
      }

      if (externalPrimaryButton) {
        var continueText = (wizardRoot.getAttribute("data-wizard-continue-text") || "Continue").toString();
        var submitText = (wizardRoot.getAttribute("data-wizard-submit-text") || "").toString();
        var internalLabel = internalContinueButton ? (internalContinueButton.textContent || "").trim() : "";
        var nextPrimaryHtml = finalVisibleStep
          ? ('<span>' + escapeButtonText(internalLabel || submitText || continueText) + '&nbsp;</span><i class="bi bi-check2"></i>')
          : ('<span>' + escapeButtonText(internalLabel || continueText || "Continue") + '&nbsp;</span><i class="bi bi-arrow-right"></i>');
        if (externalPrimaryButton.innerHTML !== nextPrimaryHtml) {
          externalPrimaryButton.innerHTML = nextPrimaryHtml;
        }
        externalPrimaryButton.disabled = !!internalContinueButton.disabled;
        var finalStepAttribute = finalVisibleStep ? "true" : "false";
        if (externalPrimaryButton.getAttribute("data-dialog-final-step") !== finalStepAttribute) {
          externalPrimaryButton.setAttribute("data-dialog-final-step", finalStepAttribute);
        }
      }
    } finally {
      syncingExternalButtons = false;
    }
  }

  var onDialogClick = async function(event) {
    var parts = resolveWizardParts();
    var internalContinueButton = parts.internalContinueButton;
    var clickedContinue = event.target && event.target.closest ? event.target.closest("button") : null;
    if (!(parts.hasContinueAction && clickedContinue === internalContinueButton)) return;
    if (!isFinalVisibleStep()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    await handleFinalSubmit();
    syncExternalButtons();
  };
  dialogDom.addEventListener("click", onDialogClick, true);

  var onExternalBackClick = null;
  if (externalBackButton) {
    onExternalBackClick = function() {
      var parts = resolveWizardParts();
      if (!(parts.hasBackAction && parts.internalBackButton && !parts.internalBackButton.disabled)) return;
      parts.internalBackButton.click();
      syncExternalButtons();
    };
    externalBackButton.addEventListener("click", onExternalBackClick);
  }

  var onExternalPrimaryClick = null;
  if (externalPrimaryButton) {
    onExternalPrimaryClick = async function() {
      var parts = resolveWizardParts();
      if (!(parts.hasContinueAction && parts.internalContinueButton && !parts.internalContinueButton.disabled)) return;
      if (isFinalVisibleStep()) {
        await handleFinalSubmit();
        syncExternalButtons();
        return;
      }
      parts.internalContinueButton.click();
      syncExternalButtons();
    };
    externalPrimaryButton.addEventListener("click", onExternalPrimaryClick);
  }

  var onVisibilitySync = function() {
    syncExternalButtons();
  };
  dialogDom.addEventListener("ua:wizard:visibility-sync", onVisibilitySync);

  function scheduleSyncExternalButtons() {
    if (syncScheduled || disconnected) return;
    syncScheduled = true;
    Promise.resolve().then(function() {
      syncScheduled = false;
      if (disconnected) return;
      syncExternalButtons();
    });
  }

  if (typeof MutationObserver === "function") {
    mutationObserver = new MutationObserver(function(mutations) {
      if (syncingExternalButtons || disconnected) return;
      var shouldSync = false;
      var parts = resolveWizardParts();
      for (var mi = 0; mi < mutations.length; mi++) {
        var mutation = mutations[mi];
        if (!mutationTargetsWizard(parts, mutation)) continue;
        if (shouldIgnoreSelfAuthoredFooterMutation(mutation)) continue;
        shouldSync = true;
        break;
      }
      if (shouldSync) scheduleSyncExternalButtons();
    });
    mutationObserver.observe(dialogDom, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "disabled", "hidden", "style", "aria-hidden", "data-wizard-current", "data-wizard-step-hidden", "data-fse-step-hidden", "data-wizard-submit-text", "data-wizard-continue-text", "id", detachedFooterMarkerAttribute]
    });
  }

  syncExternalButtons();

  return {
    wizardRoot: function() {
      var parts = resolveWizardParts();
      return parts.wizardRoot;
    },
    sync: syncExternalButtons,
    isFinalVisibleStep: isFinalVisibleStep,
    disconnect: function() {
      disconnected = true;
      if (mutationObserver && typeof mutationObserver.disconnect === "function") {
        mutationObserver.disconnect();
      }
      dialogDom.removeEventListener("click", onDialogClick, true);
      dialogDom.removeEventListener("ua:wizard:visibility-sync", onVisibilitySync);
      if (externalBackButton && onExternalBackClick) {
        externalBackButton.removeEventListener("click", onExternalBackClick);
      }
      if (externalPrimaryButton && onExternalPrimaryClick) {
        externalPrimaryButton.removeEventListener("click", onExternalPrimaryClick);
      }
    }
  };
}

function getBestListRowLabel(value, rowIndex, listState, entry) {
  var fallbackTitle = safeDisplayText((listState && listState.itemSpec && listState.itemSpec.title) || (entry && entry.title) || "Item", "Item");
  var prefix = fallbackTitle === "Item" ? "Item" : fallbackTitle.replace(/\s+Editor$/i, "").trim();
  var positionLabel = prefix + " " + (Number(rowIndex) + 1);
  if (value === undefined || value === null) return positionLabel;

  if (!(value && typeof value === "object") || Array.isArray(value)) {
    var primitiveLabel = safeDisplayText(value, "").trim();
    return primitiveLabel ? (positionLabel + ": " + primitiveLabel) : positionLabel;
  }

  var fieldDefinitionLabel = summarizeFieldDefinitionValue(value);
  if (fieldDefinitionLabel) {
    return positionLabel + ": " + fieldDefinitionLabel;
  }

  var candidateKeys = ["heading", "title", "label", "name", "kind", "src", "href", "reusablePartPath"];
  for (var i = 0; i < candidateKeys.length; i++) {
    var raw = safeDisplayText(value[candidateKeys[i]], "").trim();
    if (!raw) continue;
    if (candidateKeys[i] === "kind") raw = humanizeNavigationSegment(raw);
    return positionLabel + ": " + raw;
  }

  return positionLabel;
}

function summarizeFieldDefinitionValue(value) {
  if (!(value && typeof value === "object") || Array.isArray(value)) return "";

  var fieldTitle = safeDisplayText(value.title, "").trim();
  var fieldName = safeDisplayText(value.name, "").trim();
  var typeValue = normalizeTypeSelection(value.type);
  var typeLabel = typeValue.length > 0 ? humanizeNavigationSegment(typeValue.join(" / ")) : "";

  var parts = [];
  if (fieldTitle) parts.push(fieldTitle);
  else if (fieldName) parts.push("." + fieldName);
  if (typeLabel) parts.push(typeLabel);

  return parts.join(" · ");
}

function findNavigationEntryForPath(path, metadata) {
  var targetPath = path instanceof Array ? path.join(".") : "";
  if (!targetPath || !(metadata && metadata.entries instanceof Array)) return null;
  for (var i = 0; i < metadata.entries.length; i++) {
    var candidate = metadata.entries[i] || {};
    if (!(candidate.path instanceof Array)) continue;
    if (candidate.path.join(".") !== targetPath) continue;
    return candidate;
  }
  return null;
}

function findNavigationTargetIdForPath(path, metadata) {
  var entry = findNavigationEntryForPath(path, metadata);
  return entry ? safeDisplayText(entry.navigationTargetId, "") : "";
}

function buildNavigationPathFromEntry(entry, metadata) {
  var sourceSegments = (metadata && typeof metadata.navigationContextSegmentsResolver === "function")
    ? metadata.navigationContextSegmentsResolver(metadata, metadata && metadata.rootNode ? metadata.rootNode : null)
    : (metadata && metadata.navigationContextSegments);
  var baseSegments = cloneNavigationSegments(sourceSegments).map(function(segment) {
    if (!(metadata && metadata.navigationContextDetached === true)) return segment;
    return Object.assign({}, segment, {
      canNavigate: false,
      stepNumber: 0
    });
  });
  if (!(entry && entry.path instanceof Array && entry.path.length)) return baseSegments;
  var segments = baseSegments.slice();
  for (var i = 0; i < entry.path.length; i++) {
    var slice = entry.path.slice(0, i + 1);
    var key = entry.path[i];
    var targetEntry = findNavigationEntryForPath(slice, metadata);
    var stepNumber = targetEntry ? Number(targetEntry.stepNumber || 0) || 0 : 0;
    var navigationTargetId = targetEntry ? safeDisplayText(targetEntry.navigationTargetId, "") : "";
    segments.push({
      key: key,
      label: i === entry.path.length - 1 ? safeDisplayText(entry.title, humanizeNavigationSegment(key)) : humanizeNavigationSegment(key),
      path: slice,
      pathLabel: "." + slice.join("."),
      navigationTargetId: navigationTargetId,
      canNavigate: !!(stepNumber || navigationTargetId),
      stepNumber: stepNumber
    });
  }
  return segments;
}

function isNavigationPathPrefix(prefix, full) {
  if (!(prefix instanceof Array) || !(full instanceof Array)) return false;
  if (prefix.length > full.length) return false;
  for (var i = 0; i < prefix.length; i++) {
    if (String(prefix[i]) !== String(full[i])) return false;
  }
  return true;
}

function injectNavigationRowSegment(segments, extras) {
  var rowPath = extras && extras.rowPath instanceof Array ? extras.rowPath.slice() : [];
  if (!(rowPath.length)) return segments;

  var nextSegments = (segments instanceof Array ? segments : []).map(function(segment) {
    return segment && typeof segment === "object" ? Object.assign({}, segment) : segment;
  });

  var rowStepNumber = Number(extras && extras.rowStepNumber || 0) || 0;
  if (!(rowStepNumber > 0)) {
    for (var prefixIndex = nextSegments.length - 1; prefixIndex >= 0; prefixIndex--) {
      var prefixSegment = nextSegments[prefixIndex] || {};
      var prefixPath = prefixSegment.path instanceof Array ? prefixSegment.path : [];
      if (!(prefixPath.length) || !(isNavigationPathPrefix(prefixPath, rowPath))) continue;
      rowStepNumber = Number(prefixSegment.stepNumber || 0) || 0;
      if (rowStepNumber > 0) break;
    }
  }

  var rowLabel = safeDisplayText((extras && extras.listRowLabel) || "", "Item");
  var rowSegment = {
    key: String(rowPath[rowPath.length - 1] || "item"),
    label: rowLabel,
    path: rowPath,
    pathLabel: "." + rowPath.join("."),
    navigationTargetId: safeDisplayText((extras && extras.rowNavigationTargetId) || "", ""),
    canNavigate: !!safeDisplayText((extras && extras.rowNavigationTargetId) || "", ""),
    stepNumber: rowStepNumber
  };

  for (var i = 0; i < nextSegments.length; i++) {
    var candidate = nextSegments[i] || {};
    var candidatePath = candidate.path instanceof Array ? candidate.path : [];
    if (candidatePath.join(".") !== rowPath.join(".")) continue;
    nextSegments[i] = Object.assign({}, candidate, rowSegment);
    return nextSegments;
  }

  var insertAt = nextSegments.length;
  for (var segmentIndex = nextSegments.length - 1; segmentIndex >= 0; segmentIndex--) {
    var segment = nextSegments[segmentIndex] || {};
    var segmentPath = segment.path instanceof Array ? segment.path : [];
    if (!(segmentPath.length) || !(isNavigationPathPrefix(segmentPath, rowPath))) continue;
    insertAt = segmentIndex + 1;
    break;
  }

  nextSegments.splice(insertAt, 0, rowSegment);
  return nextSegments;
}

function buildSchemaEditorNavigationState(metadata, entry, extras) {
  var detail = extras && typeof extras === "object" ? Object.assign({}, extras) : {};
  var segments = injectNavigationRowSegment(buildNavigationPathFromEntry(entry, metadata), detail);
  detail.editorId = metadata && metadata.editorId ? metadata.editorId : "";
  detail.editorName = metadata && metadata.editorName ? metadata.editorName : "";
  detail.editorLabel = metadata && metadata.editorLabel ? metadata.editorLabel : "Schema Editor";
  detail.rootEditorName = metadata && metadata.rootEditorName ? metadata.rootEditorName : detail.editorName;
  detail.rootEditorLabel = metadata && metadata.rootEditorLabel ? metadata.rootEditorLabel : detail.editorLabel;
  detail.entryIndex = entry ? entry.index : null;
  detail.entryTitle = entry ? safeDisplayText(entry.title, entry.key || "Field") : "Field";
  detail.path = entry && entry.path ? entry.path.slice() : [];
  detail.pathLabel = entry && entry.pathLabel ? entry.pathLabel : (detail.path.length ? "." + detail.path.join(".") : "");
  detail.segments = segments;
  detail.labelTrail = segments.map(function(segment) { return segment.label; });
  return detail;
}

function emitNavigationState(root, metadata, entry, extras) {
  if (!root || !(entry && metadata)) return;
  var detail = buildSchemaEditorNavigationState(metadata, entry, extras);
  try {
    var navigationEvent = createDomEvent(root, "ua:schema-editor:navigation", {
      bubbles: true,
      detail: detail
    });
    if (navigationEvent) root.dispatchEvent(navigationEvent);
  } catch (error) {}
}

function bindNavigationTracking(root, metadata) {
  if (!root || !metadata || !(metadata.entries instanceof Array)) return;
  if (root.__fseNavigationBound) return;
  root.__fseNavigationBound = true;
  var lastNavigationSignature = "";

  function findEntryForNode(node) {
    var current = node;
    while (current && current !== root) {
      if (current.getAttribute) {
        var entryIndex = current.getAttribute("data-fse-entry-index");
        if (entryIndex !== null && entryIndex !== undefined && entryIndex !== "") {
          var numeric = Number(entryIndex);
          for (var i = 0; i < metadata.entries.length; i++) {
            if (metadata.entries[i].index === numeric) return metadata.entries[i];
          }
        }
      }
      current = current.parentNode;
    }
    return null;
  }

  function describeNodeContext(node, entry) {
    var current = node;
    var extras = {};
    while (current && current !== root) {
      if (current.getAttribute) {
        var rowBase = current.getAttribute("data-fse-list-row");
        if (rowBase) {
          var rowIndex = -1;
          var rowState = null;
          if (entry && entry.descriptor && entry.descriptor.listStates) {
            var listStates = entry.descriptor.listStates;
            var listKeys = Object.keys(listStates);
            for (var listKeyIndex = 0; listKeyIndex < listKeys.length; listKeyIndex++) {
              var listState = listStates[listKeys[listKeyIndex]];
              if (!(listState && listState.rows instanceof Array)) continue;
              for (var i = 0; i < listState.rows.length; i++) {
                if (listState.rows[i].rowBase === rowBase) {
                  rowIndex = i;
                  rowState = listState.rows[i];
                  break;
                }
              }
              if (rowState) break;
            }
          }
          extras.listRowIndex = rowIndex;
          extras.listRowLabel = rowIndex >= 0 ? getBestListRowLabel(getCurrentListRowValue(root, rowState), rowIndex, listState, entry) : "Item";
          extras.rowBase = rowBase;
          extras.rowPath = rowState && rowState.rowPath instanceof Array ? rowState.rowPath.slice() : [];
          extras.rowNavigationTargetId = rowState && rowState.navigationTargetId ? rowState.navigationTargetId : (current.id || "");
          break;
        }
      }
      current = current.parentNode;
    }
    return extras;
  }

  function announce(node, kind) {
    var entry = findEntryForNode(node);
    if (!entry) return;
    var extras = describeNodeContext(node, entry);
    extras.kind = kind || "focus";
    var previewState = buildSchemaEditorNavigationState(metadata, entry, extras);
    var signature = JSON.stringify({
      path: previewState.path,
      labelTrail: previewState.labelTrail,
      rowPath: previewState.rowPath,
      listRowLabel: previewState.listRowLabel,
      rootEditorName: previewState.rootEditorName
    });
    if (signature === lastNavigationSignature) return;
    lastNavigationSignature = signature;
    emitNavigationState(root, metadata, entry, extras);
  }

  root.addEventListener("focusin", function(event) {
    announce(event.target, "focus");
  });
  root.addEventListener("click", function(event) {
    announce(event.target, "click");
  });
  root.addEventListener("change", function(event) {
    announce(event.target, "change");
  });

  if (metadata.entries.length > 0) {
    emitNavigationState(root, metadata, metadata.entries[0], { kind: "initial" });
  }
}

function bindResetButtons(root, metadata) {
  if (!root || !metadata || !metadata.entries) return;

  var buttons = root.querySelectorAll("[data-fse-reset]");
  for (var i = 0; i < buttons.length; i++) {
    var button = buttons[i];
    if (button.__fseResetBound) continue;
    button.__fseResetBound = true;

    (function (btn) {
      btn.addEventListener("click", async function () {
        var entryIndex = Number(btn.getAttribute("data-fse-reset"));
        var resetType = (btn.getAttribute("data-fse-reset-type") || "").trim();
        if (Number.isNaN(entryIndex) || !resetType) return;

        var entry = null;
        for (var e = 0; e < metadata.entries.length; e++) {
          if (metadata.entries[e].index === entryIndex) {
            entry = metadata.entries[e];
            break;
          }
        }
        if (!entry || !entry.descriptor) return;

        var defaultValue = getEntryDefaultValue(entry, metadata.entries);
        if (defaultValue !== undefined) {
          defaultValue = cloneData(defaultValue);
        }

        if (root && root.__uaSchemaEditor) {
          root.__uaSchemaEditor.sourceData = root.__uaSchemaEditor.sourceData && typeof root.__uaSchemaEditor.sourceData === "object"
            ? root.__uaSchemaEditor.sourceData
            : {};

          if (defaultValue === undefined) {
            deleteByPath(root.__uaSchemaEditor.sourceData, entry.path);
          } else {
            setByPath(root.__uaSchemaEditor.sourceData, entry.path, cloneData(defaultValue));
          }
        }

        await applyEntryValue(root, metadata, entry, defaultValue);
        notifyEditorMutation(root, {
          kind: "reset",
          path: entry.path,
          resetType: inferTypeFromValue(defaultValue, entry.types)
        });
      });
    })(button);
  }
}

function getEmptyValueForType(type) {
  var normalizedType = normalizeSingleType(type);
  if (normalizedType === "object" || normalizedType === "inner" || type === FIELD_SCHEMA_DOCUMENT_PSEUDO_TYPE) return {};
  if (normalizedType === "list") return [];
  if (normalizedType === "boolean") return false;
  return "";
}

function convertValueForSave(rawValue, selectedType, spec) {
  var type = normalizeSingleType(selectedType);

  if (type === "datetime") {
    rawValue = fromHumanDatetimeValue(rawValue);
  }

  if (isBlank(rawValue)) {
    return undefined;
  }

  if (type === "number") {
    var asNumber = Number(rawValue);
    return Number.isNaN(asNumber) ? rawValue : asNumber;
  }

  if (type === "boolean") {
    if (typeof rawValue === "boolean") {
      return rawValue;
    }

    if (typeof rawValue === "string") {
      var normalized = rawValue.toLowerCase().trim();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
    }

    return !!rawValue;
  }

  if (type === "list") {
    return Array.isArray(rawValue) ? rawValue : [];
  }

  if (type === "object" || type === "inner") {
    return (rawValue && typeof rawValue === "object") ? rawValue : {};
  }

  if (type === "any") {
    return rawValue;
  }

  return rawValue;
}

function createValidationError(message, name) {
  return {
    type: "validation",
    message: message,
    input: {
      name: name
    }
  };
}

function hasTypeIntersection(allowedTypes, selectedTypes) {
  if (!(allowedTypes instanceof Array) || allowedTypes.length === 0) return true;
  if (!(selectedTypes instanceof Array) || selectedTypes.length === 0) return false;

  for (var i = 0; i < allowedTypes.length; i++) {
    if (selectedTypes.indexOf(normalizeSingleType(allowedTypes[i])) >= 0) {
      return true;
    }
  }
  return false;
}

function getEffectiveFieldTypeSelection(control, state, documentData) {
  if (!state || state.isFieldSchemaMode !== true) return [];
  if (state.fieldTypeEntryIndex === null || state.fieldTypeEntryIndex === undefined) return [];

  var typeEntry = getFieldTypeEntry(state);
  if (!(typeEntry && typeEntry.descriptor)) return [];

  var typeControlName = typeEntry.descriptor.controlNames[typeEntry.types[0]];
  var typeRaw = typeControlName ? documentData[typeControlName] : undefined;

  if (typeControlName && typeRaw === undefined) {
    typeRaw = readControlValueFromDom(control, typeControlName);
  }

  if (typeRaw === undefined && typeEntry.values) {
    var cachedType = typeEntry.values.field.type;
    if (cachedType === undefined) {
      cachedType = typeEntry.values[typeEntry.types[0]];
    }
    if (cachedType !== undefined) {
      typeRaw = cachedType;
    }
  }

  if (typeRaw === undefined) {
    typeRaw = typeEntry.descriptor.initialValue;
  }

  if (typeRaw === undefined && typeEntry.spec && Object.prototype.hasOwnProperty.call(typeEntry.spec, "default")) {
    typeRaw = typeEntry.spec.default;
  }

  return normalizeTypeSelection(typeRaw);
}

exports.render = async function (options) {
  var schema = (options && options.schema && typeof options.schema === "object") ? options.schema : {};
  if (Object.keys(schema).length === 0 && options && options.fields && typeof options.fields === "object") {
    schema = options.fields;
  }

  var data = (options && options.data && typeof options.data === "object") ? options.data : {};
  if (Object.keys(data).length === 0 && options && options.value && typeof options.value === "object") {
    data = options.value;
  }

  var schemaDocumentMode = options && options.schemaDocumentMode === true;
  if (schemaDocumentMode) {
    var schemaDocument = isFieldSchemaDocument(schema) ? schema : {};
    var fieldDefinitionSchema = buildFieldDefinitionEditorSchema(
      buildFieldCatalog(),
      buildFieldAttributeMap(),
      buildFieldTypeAttributeDefinitions(),
      buildGlobalAttributeDefinitions()
    );

    schema = {
      fields: {
        title: "Fields",
        description: "Define the schema fields.",
        type: "list",
        of: "object",
        fields: fieldDefinitionSchema,
        schemaDocument: schemaDocument,
        default: convertSchemaDocumentToFieldList(schemaDocument)
      }
    };
    data = isFieldSchemaDocument(data)
      ? {
        fields: convertSchemaDocumentToFieldList(data)
      }
      : {};
  }

  var fieldMode = false;
  if (Object.keys(schema).length === 0 && options && options.type) {
    var fieldSpec = Object.assign({}, options);
    delete fieldSpec.n;
    delete fieldSpec.namespace;
    delete fieldSpec.name;
    delete fieldSpec.value;
    delete fieldSpec.data;
    delete fieldSpec.schema;
    delete fieldSpec.groups;
    delete fieldSpec.continueText;
    fieldSpec = stripUndefinedProperties(fieldSpec);
    schema = {
      value: fieldSpec
    };

    if (options.value !== undefined) {
      data = { value: options.value };
    } else if (options.data !== undefined && typeof options.data !== "object") {
      data = { value: options.data };
    }
    fieldMode = true;
  }
  var stableEditorSeed = buildStableEditorSeed(options);
  var editorId = stableEditorSeed || uai.generateRandomID(10);
  var wizardName = "ua.fields.schema.editor." + editorId;
  var wizardDomId = "ua-schema-editor-wizard-" + editorId;
  var shellDomId = "ua-schema-editor-shell-" + editorId;
  var navigationTargetPrefix = "ua-schema-editor-nav-" + editorId + "-";

  var entries = [];
  var isFieldSchemaMode = hasFieldSchemaCore(schema);
  var typeCatalog = buildFieldCatalog();
  var typeAttributeMap = buildFieldAttributeMap();
  var typeAttributeDefinitions = buildFieldTypeAttributeDefinitions();
  var globalAttributeDefinitions = buildGlobalAttributeDefinitions();

  if (isFieldSchemaMode) {
    schema = enrichFieldSchemaForEditor(
      schema,
      typeCatalog,
      typeAttributeMap,
      typeAttributeDefinitions,
      globalAttributeDefinitions
    );
  }

  if (isFieldSchemaMode && schema.type && typeof schema.type === "object") {
    schema.type = Object.assign({}, schema.type, {
      type: "field.type",
      typesCatalog: typeCatalog
    });
  }

  buildEntries(schema, [], entries);
  for (var ei = 0; ei < entries.length; ei++) {
    entries[ei].visibleForTypes = isFieldSchemaMode ? inferEntryVisibleTypes(entries[ei], typeAttributeMap) : [];
    entries[ei].navigationTargetId = navigationTargetPrefix + entries[ei].index;
  }

  var shell = {
    n: "div",
    id: shellDomId,
    c: "ua-schema-editor-shell",
    i: []
  };

  if (entries.length === 0) {
    shell.i.push({
      n: "div",
      c: "alert alert-warning mb-0",
      i: "No schema fields were provided."
    });

    var emptyDom = await jsonRender.render(shell);
    emptyDom.__uaSchemaEditor = {
      editorId: editorId,
      wizardName: wizardName,
      wizardDomId: wizardDomId,
      shellDomId: shellDomId,
      schema: schema,
      entries: [],
      schemaDocumentMode: schemaDocumentMode
    };

    return emptyDom;
  }

  var wizard = {
    n: "ua.wizard",
    id: wizardDomId,
    name: wizardName,
    continueText: options && options.continueText ? options.continueText : "Continue",
    submitText: options && options.submitText ? options.submitText : "",
    showStepNavigation: options && options.showStepNavigation === false ? false : true,
    showSummaryStep: options && options.showSummaryStep === false ? false : true,
    disableDefaultBehavior: options && options.disableDefaultWizardBehavior === true ? true : false,
    inner: []
  };

  for (var i = 0; i < entries.length; i++) {
    var entry = entries[i];
    var initialValue = getByPath(data, entry.path);
    entry.defaultValue = getEntryDefaultValue(entry, entries);
    entry.hasResetDefault = entry.defaultValue !== undefined;
    entry.isDefaultValue = false;
    if (initialValue === undefined && entry.hasResetDefault) {
      initialValue = cloneData(entry.defaultValue);
      entry.isDefaultValue = true;
    }

    entry.descriptor = prepareControlDescriptor(entry, editorId, initialValue);
    entry.stateEntries = entries;
    entry.values = {};
    if (initialValue !== undefined) {
      entry.values[entry.descriptor.initialType] = cloneData(initialValue);
    }
  }

  var groupsInput = [];
  if (options && options.groups instanceof Array) {
    groupsInput = options.groups;
  } else if (isFieldSchemaMode) {
    groupsInput = deriveDynamicFieldSchemaGroups(schema, typeCatalog, typeAttributeMap);
  } else {
    groupsInput = deriveDefaultGroupsForSchema(schema);
  }
  var stepPlan = buildGroupPlan(entries, groupsInput, {
    skipGroupedNestedSteps: options && options.skipGroupedNestedSteps === true,
    excludeUngroupedEntries: options && options.excludeUngroupedEntries === true
  });
  if (!(stepPlan instanceof Array) || stepPlan.length === 0) {
    stepPlan = [];
    for (var p = 0; p < entries.length; p++) {
      stepPlan.push({
        title: safeDisplayText(entries[p].title, entries[p].key || ("Step " + (p + 1))),
        description: "",
        entries: [entries[p]],
        visibleForTypes: []
      });
    }
  }

  for (var s = 0; s < stepPlan.length; s++) {
    var planStep = stepPlan[s];
    var stepNumber = s + 1;
    var stepTitle = safeDisplayText(planStep.title, "Step " + stepNumber);
    var stepDescription = safeDisplayText(planStep.description, "");

    var sectionList = [];
    var compactMode = planStep.entries.length > 1;
    for (var se = 0; se < planStep.entries.length; se++) {
      planStep.entries[se].stepNumber = stepNumber;
      sectionList.push(buildEntrySection(planStep.entries[se], compactMode));
    }

    var step = {
      n: "div",
      "data-wizard-title": stepTitle,
      wizardHeader: {
        icon: "diagram-3",
        title: stepTitle,
        description: stepDescription || "Complete this section before continuing."
      },
      i: sectionList
    };

    if (planStep.visibleForTypes instanceof Array && planStep.visibleForTypes.length > 0) {
      step["data-fse-step-types"] = planStep.visibleForTypes.join(",");
    }
    wizard.inner.push(step);
  }

  shell.i.push(wizard);

  var dom = await jsonRender.render(shell);

  dom.__uaSchemaEditor = {
    editorId: editorId,
    editorName: (options && options.name) ? options.name : "",
    editorLabel: safeDisplayText((options && (options.title || options.label)) || ((options && options.name) || "Schema Editor"), "Schema Editor"),
    rootEditorName: safeDisplayText((options && options.rootEditorName) || ((options && options.name) || ""), ""),
    rootEditorLabel: safeDisplayText((options && options.rootEditorLabel) || ((options && (options.title || options.label)) || ((options && options.name) || "Schema Editor")), "Schema Editor"),
    navigationContextSegments: cloneNavigationSegments(options && options.navigationContextSegments),
    navigationContextSegmentsResolver: (options && typeof options.navigationContextSegmentsResolver === "function") ? options.navigationContextSegmentsResolver : null,
    navigationContextDetached: options && options.navigationContextDetached === true,
    wizardName: wizardName,
    wizardDomId: wizardDomId,
    shellDomId: shellDomId,
    schema: schema,
    entries: entries,
    sourceData: data,
    fieldMode: fieldMode,
    schemaDocumentMode: schemaDocumentMode,
    isFieldSchemaMode: isFieldSchemaMode,
    rootNode: dom,
    fieldTypeEntryIndex: (function() {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].path.length === 1 && entries[i].key === "type") {
          return entries[i].index;
        }
      }
      return null;
    })()
  };

  bindTypeSelectors(dom, dom.__uaSchemaEditor);
  bindFieldTypeSelectorControl(dom, dom.__uaSchemaEditor);
  bindFieldTypeVisibility(dom, dom.__uaSchemaEditor);
  bindResetButtons(dom, dom.__uaSchemaEditor);
  bindObjectDialogEditors(dom, dom.__uaSchemaEditor);
  bindNavigationTracking(dom, dom.__uaSchemaEditor);
  await bindListEditors(dom, dom.__uaSchemaEditor, data);

  return dom;
};

exports.load = async function (name, control, data) {
  var state = control ? control.__uaSchemaEditor : null;

  if (!state || !state.entries || state.entries.length === 0) {
    return;
  }

  if (state.fieldMode) {
    state.sourceData = { value: data };
  } else if (state.schemaDocumentMode) {
    state.sourceData = isFieldSchemaDocument(data)
      ? { fields: convertSchemaDocumentToFieldList(data) }
      : {};
  } else {
    state.sourceData = (data && typeof data === "object") ? data : {};
  }

  var loadObject = {};

  for (var i = 0; i < state.entries.length; i++) {
    var entry = state.entries[i];
    if (entry.nestedOnly) {
      continue;
    }

    var value = getByPath(state.sourceData || {}, entry.path);
    if (value === undefined) {
      value = getEntryDefaultValue(entry, state.entries);
    }

    var selectedType = entry.types[0];
    if (value !== undefined) {
      selectedType = inferTypeFromValue(value, entry.types);
    }

    clearEntryListState(control, entry, selectedType);
    rebuildEntryBranchCache(entry, value, selectedType);

    if (entry.descriptor.typeSelectorName) {
      loadObject[entry.descriptor.typeSelectorName] = selectedType;
    }

    if (entry.descriptor.objectDialogs) {
      var objectDialogTypes = Object.keys(entry.descriptor.objectDialogs);
      for (var od = 0; od < objectDialogTypes.length; od++) {
        var objectDialogType = objectDialogTypes[od];
        entry.descriptor.objectDialogs[objectDialogType].value = {};
        updateObjectEntrySummary(control, entry.descriptor, objectDialogType, {});
      }
    }

    if (value === undefined) {
      var inlineObjectType = getInlineObjectControlType(entry);
      var detachedObjectType = getDetachedObjectControlType(entry);
      var resetObjectType = inlineObjectType || detachedObjectType;
      var shouldClearInactiveBranches = true;
      if (resetObjectType) {
        var emptyObjectControlName = entry.descriptor.controlNames[resetObjectType];
        if (emptyObjectControlName) {
          loadObject[emptyObjectControlName] = {};
        }
        if (entry.descriptor.typeSelectorName) {
          loadObject[entry.descriptor.typeSelectorName] = resetObjectType;
          selectedType = resetObjectType;
        }
      }

      if (shouldClearInactiveBranches) {
        for (var emptyTypeIndex = 0; emptyTypeIndex < entry.types.length; emptyTypeIndex++) {
          var emptyType = entry.types[emptyTypeIndex];
          if (emptyType === selectedType) continue;

          var emptyControlName = entry.descriptor.controlNames[emptyType];
          if (emptyControlName) {
            loadObject[emptyControlName] = getEmptyValueForType(emptyType);
          }
        }
      }
      continue;
    }

    if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
      entry.descriptor.objectDialogs[selectedType].value = (value && typeof value === "object") ? value : {};
      updateObjectEntrySummary(control, entry.descriptor, selectedType, entry.descriptor.objectDialogs[selectedType].value);
      continue;
    }

    var controlName = entry.descriptor.controlNames[selectedType] || entry.descriptor.controlNames[entry.types[0]];

    if (selectedType === "list") {
      continue;
    }

    if (controlName) {
      loadObject[controlName] = value;
    }
  }

  await window.UATools.load(loadObject, "#" + state.wizardDomId);
  bindTypeSelectors(control, state);
  bindFieldTypeSelectorControl(control, state);
  bindFieldTypeVisibility(control, state);
  bindResetButtons(control, state);
  bindObjectDialogEditors(control, state);
  bindNavigationTracking(control, state);
  await bindListEditors(control, state, state.sourceData);

  var wizardRoot = control.querySelector("[data-wizard='true']");
  var hasLoadedDocument = !!(state.sourceData && typeof state.sourceData === "object" && Object.keys(state.sourceData).length > 0);
  if (wizardRoot && hasLoadedDocument) {
    var totalWizardSteps = Number(wizardRoot.getAttribute("data-wizard-steps") || "0");
    var allSeenSteps = [];
    for (var stepIndex = 1; stepIndex <= totalWizardSteps; stepIndex++) {
      allSeenSteps.push(stepIndex);
    }

    wizardRoot.setAttribute("data-wizard-editing", "true");
    wizardRoot.setAttribute("data-wizard-nav-free", "true");
    wizardRoot.setAttribute("data-wizard-hasSeen", JSON.stringify(allSeenSteps));

    if (typeof wizardRoot.dispatchEvent === "function") {
      var loadedEditEvent = createDomEvent(wizardRoot, "ua:wizard:visibility-sync", {
        bubbles: false,
        detail: {
          source: "ua.fields.schema.editor.load",
          editing: true
        }
      });
      if (loadedEditEvent) wizardRoot.dispatchEvent(loadedEditEvent);
    }
  }
};

exports.save = async function (name, control, repo) {
  var state = control ? control.__uaSchemaEditor : null;

  if (!state) {
    return {
      success: false,
      namespace: exports.define.namespace,
      data: {},
      errors: [createValidationError("Schema editor state is unavailable.", name)]
    };
  }

  var saveSelector = "#" + (state.shellDomId || state.wizardDomId);
  var saveResult = await window.UATools.save(saveSelector, true);
  if (!saveResult || saveResult.success === false) {
    return {
      success: false,
      namespace: exports.define.namespace,
      data: {},
      errors: (saveResult && saveResult.errors) ? saveResult.errors : [createValidationError("Unable to save schema editor.", name)]
    };
  }

  var wizardData = saveResult[state.wizardName] || null;
  if (!wizardData) {
    var keys = Object.keys(saveResult || {});
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (key === "success") continue;
      if (saveResult[key] && saveResult[key].namespace === "ua.wizard") {
        wizardData = saveResult[key];
        break;
      }
    }
  }

  if (!wizardData || !wizardData.data) {
    return {
      success: false,
      namespace: exports.define.namespace,
      data: {},
      errors: [createValidationError("No wizard payload was returned by the schema editor.", name)]
    };
  }
  var documentData = wizardData.data.document || wizardData.data || {};
  var output = {};
  var errors = [];
  var selectedFieldTypes = getEffectiveFieldTypeSelection(control, state, documentData);

  for (var e = 0; e < state.entries.length; e++) {
    var entry = state.entries[e];

    if (entry.nestedOnly) {
      continue;
    }

    if (!isMixedInlineObjectChildActive(control, state, entry)) {
      continue;
    }

    if (!hasTypeIntersection(entry.visibleForTypes, selectedFieldTypes)) {
      continue;
    }

    var selectedType = entry.types[0];

    if (entry.descriptor.typeSelectorName) {
      selectedType = documentData[entry.descriptor.typeSelectorName] || selectedType;
    }

    var valueName = entry.descriptor.controlNames[selectedType];
    if (selectedType === "list") {
      var listState = entry.descriptor.listStates[selectedType];
      var listValues = [];
      var listDefaults = Array.isArray(entry.spec && entry.spec.default) ? entry.spec.default : null;

      if (listState && Array.isArray(listState.rows)) {
        for (var r = 0; r < listState.rows.length; r++) {
          var row = listState.rows[r];
          var rowType = row.rowTypes[0];
          if (row.rowTypeName) {
            rowType = documentData[row.rowTypeName] || rowType;
          }

          var rowDefaultValue = (listDefaults && r < listDefaults.length)
            ? cloneData(listDefaults[r])
            : undefined;
          var rowDefaultType = rowDefaultValue === undefined
            ? ""
            : inferTypeFromValue(rowDefaultValue, row.rowTypes);

          var rowValueName = row.controlNames[rowType];
          var rowRawValue = documentData[rowValueName];

          if (
            (rowRawValue === undefined || rowRawValue === null || rowRawValue === "") &&
            row.values &&
            Object.prototype.hasOwnProperty.call(row.values, rowType)
          ) {
            rowRawValue = row.values[rowType];
          }

          if (row.pending === true && isPendingBlankListValue(rowRawValue, rowType)) {
            var pendingConverted = convertValueForSave(rowRawValue, rowType, entry.spec);
            if (isBlank(pendingConverted)) {
              continue;
            }
            if (isObjectLikeEditorType(rowType) && isEmptyObjectValue(pendingConverted)) {
              continue;
            }
            if (Array.isArray(pendingConverted) && pendingConverted.length === 0) {
              continue;
            }
            listValues.push(pendingConverted);
            continue;
          }

          if (isBlank(rowRawValue)) {
            continue;
          }

          try {
            var convertedRowValue = convertValueForSave(rowRawValue, rowType, entry.spec);
            if (isObjectLikeEditorType(rowType) && isEmptyObjectValue(convertedRowValue)) {
              if (
                rowDefaultType === rowType &&
                rowDefaultValue &&
                typeof rowDefaultValue === "object" &&
                !Array.isArray(rowDefaultValue)
              ) {
                convertedRowValue = rowDefaultValue;
              } else {
                continue;
              }
            }
            listValues.push(convertedRowValue);
          } catch (rowError) {
            errors.push(createValidationError(
              "Failed to parse list item for " + entry.pathLabel + ": " + rowError.message,
              rowValueName
            ));
          }
        }
      }

      if (entry.required && listValues.length === 0) {
        errors.push(createValidationError("This list requires at least one item.", entry.pathLabel));
      } else if (
        Object.prototype.hasOwnProperty.call(entry.spec || {}, "default") &&
        valuesEqual(listValues, entry.spec.default)
      ) {
        continue;
      } else if (listValues.length > 0 || entry.required) {
        setByPath(output, entry.path, listValues);
      }

      continue;
    }

    if (selectedType === "object" && entry.descriptor.objectDialogs && entry.descriptor.objectDialogs[selectedType]) {
      var dialogValue = entry.descriptor.objectDialogs[selectedType].value;
      if (isBlank(dialogValue) || summarizeObjectValue(dialogValue) === "Not configured") {
        if (entry.required) errors.push(createValidationError("This field is required.", entry.pathLabel));
        continue;
      }
      if (
        Object.prototype.hasOwnProperty.call(entry.spec || {}, "default") &&
        valuesEqual(dialogValue, entry.spec.default)
      ) {
        continue;
      }
      setByPath(output, entry.path, dialogValue);
      continue;
    }

    var rawValue = documentData[valueName];

    if (
      valueName &&
      (rawValue === undefined || rawValue === null || rawValue === '')
    ) {
      var liveDomValue = readControlValueFromDom(control, valueName);
      if (liveDomValue !== undefined) {
        rawValue = liveDomValue;
      }
    }

    if (
      valueName &&
      entry.descriptor.controlNamespaces &&
      entry.descriptor.controlNamespaces[selectedType] === 'ua.fields.schema.editor' &&
      (rawValue === undefined || rawValue === null || rawValue === '') &&
      entry.values &&
      Object.prototype.hasOwnProperty.call(entry.values, selectedType)
    ) {
      rawValue = entry.values[selectedType];
    }

    if (
      valueName &&
      entry.descriptor.controlNamespaces &&
      entry.descriptor.controlNamespaces[selectedType] === 'ua.fields.schema.editor' &&
      isObjectLikeEditorType(selectedType) &&
      isEmptyObjectValue(rawValue) &&
      entry.values &&
      Object.prototype.hasOwnProperty.call(entry.values, selectedType) &&
      !isEmptyObjectValue(entry.values[selectedType])
    ) {
      rawValue = entry.values[selectedType];
    }

    if (isBlank(rawValue)) {
      if (entry.required) {
        errors.push(createValidationError("This field is required.", valueName));
      }
      continue;
    }

    try {
      var converted = convertValueForSave(rawValue, selectedType, entry.spec);
      if (
        Object.prototype.hasOwnProperty.call(entry.spec || {}, "default") &&
        valuesEqual(converted, entry.spec.default)
      ) {
        continue;
      }
      if (selectedType === "object" && isEmptyObjectValue(converted)) {
        if (entry.required) {
          errors.push(createValidationError("This field is required.", valueName));
        }
        continue;
      }
      if (!isBlank(converted) || entry.required) {
        setByPath(output, entry.path, converted);
      }
    } catch (error) {
      errors.push(createValidationError(
        "Failed to parse field " + entry.pathLabel + ": " + error.message,
        valueName
      ));
    }
  }

  if (errors.length > 0) {
    return {
      success: false,
      namespace: exports.define.namespace,
      data: output,
      errors: errors
    };
  }

  pruneOutputDefaults(output, state.entries);

  if (state.schemaDocumentMode) {
    errors = errors.concat(collectSchemaDocumentFieldListErrors(output.fields || []));
    if (errors.length > 0) {
      return {
        success: false,
        namespace: exports.define.namespace,
        data: output,
        errors: errors
      };
    }
  }

  repo.success = true;
  repo.namespace = exports.define.namespace;
  if (state.fieldMode) {
    repo.data = output.value;
  } else if (state.schemaDocumentMode) {
    repo.data = convertFieldListToSchemaDocument(output.fields || []);
  } else {
    repo.data = output;
  }
  repo.errors = [];

  return repo;
};

exports._test = {
  hasFieldSchemaDocumentEntry: hasFieldSchemaDocumentEntry,
  isFieldSchemaDocument: isFieldSchemaDocument,
  convertSchemaDocumentToFieldList: convertSchemaDocumentToFieldList,
  convertFieldListToSchemaDocument: convertFieldListToSchemaDocument,
  collectSchemaDocumentFieldListErrors: collectSchemaDocumentFieldListErrors,
  getCurrentListRowValue: getCurrentListRowValue,
  getBestListRowLabel: getBestListRowLabel,
  getEntryDefaultValue: getEntryDefaultValue,
  pruneOutputDefaults: pruneOutputDefaults,
  normalizeFieldAttributeDefinitionForType: normalizeFieldAttributeDefinitionForType,
  setByPath: setByPath,
  normalizeStableEditorToken: normalizeStableEditorToken,
  buildStableEditorSeed: buildStableEditorSeed,
  bindDetachedWizardDialogActions: bindDetachedWizardDialogActions,
  bindDetachedDialogErrorRecovery: bindDetachedDialogErrorRecovery,
  saveDetachedSchemaEditor: saveDetachedSchemaEditor,
  formatDetachedDialogErrors: formatDetachedDialogErrors,
  clearDetachedDialogError: clearDetachedDialogError,
  showDetachedDialogError: showDetachedDialogError,
  findSchemaEditorRoot: findSchemaEditorRoot,
  readControlValueFromDom: readControlValueFromDom,
  collectLiveEditorData: collectLiveEditorData,
  getFieldTypeSpecificPresentation: getFieldTypeSpecificPresentation,
  summarizeFieldDefinitionValue: summarizeFieldDefinitionValue
};
